// src/contactLog.js
// ═══════════════════════════════════════════════════════════════
// CALL & EMAIL LOG → RESPONSES, ONE PER PERSON (10/10/26)
// ═══════════════════════════════════════════════════════════════
// Reece, 10/10: cost per call / cost per response should count "web leads,
// emails, or any sort of responses", one per person — "make sure the
// responses are at least fairly aligned with this information" (his
// RingCentral export, call_log_by_number.csv) and "include this log that
// should account for emails that should be counted as responses from several
// borrowers as well" (unified_contacts.csv = the call log + his work email).
// The CRM itself only ever recorded ~21 responses (responded_at); the log has
// ~178 people. Mail-list leads carry no phone, so most of those people can
// only be lined up with a lead by name + place — and that is never automatic.
//
// The engine (no React, no network — the screen is src/views/ContactLog.jsx):
//   readContactCsv     either export, detected by its HEADERS (any column
//                      order, extra columns ignored; quoted commas /
//                      newlines, CRLF, BOM, ; or tab) → normalized rows
//   buildPeople        rows → classified rows → PEOPLE (union-find) → kind,
//                      first date, method, reasons, facts
//   checkFlags         the judgement calls the rules can't settle ("Check
//                      these") — generic rules, never names
//   matchPeople        each person → a lead / pipeline file: sure (a phone or
//                      email on the record, a skip-traced number) / likely
//                      (name + city / state / street — needs his click) / none
//   lookupRemote       the people not found in memory, asked of the database
//                      in small batches through lib.js (never a table load)
//                      — as KEYS, each asked once a session (lookupMemory)
//   mergeImport        a new import into the stored people BY KEY (phones /
//                      emails / refs — never a number only said in a
//                      voicemail): never a duplicate, his decisions kept
//   withMatch          the stored link follows the live match and his
//                      answers (a sure match is never downgraded — unless
//                      another record turns out to share its number; "None
//                      of these" / undo clear it; likely lead ids are kept)
//   leadResponsePatch  "Mark N leads as responded" — never moves an EARLIER
//                      responded_at later
//   responsesForCost   ONE record per person across the CRM's own responses
//                      (responded / web intake) and the log — for cost per
//                      response (the next build); a probable double count
//                      (a likely match to a CRM responder) is flagged
//                      (possibleDupOf), never merged
//   load / save        table contact_responses, one row per person, per-row
//                      upserts, a localStorage mirror and the "Saved on this
//                      device only" latch exactly like src/whiteboard.js. The
//                      Supabase client is PASSED IN (db), so this module never
//                      reaches the network by itself. Decisions are stamped
//                      per key, so two copies of one person (tabs, devices)
//                      merge key by key; same-key copies fold into one.
//
// The rules below were checked row by row against his real export (231 rows
// → 178 people: 159 responses + 7 files + 12 removals; Call 138 / Call +
// Email 11 / Email 29) — change them only with a test.

import { segmentOf, normalizeMethod, applyResponse, responseDay, RESPONSE_METHODS } from './responses';
import { placeFromAddress, contactEntry } from './lib';
import { CONTACT_LOG_MAX } from './constants';

// ═══════ constants ═══════
// Reece's own line (the export lists it as "self-number"); App adds the
// Settings admin phone / RingCentral SMS number when they are set.
export const OWN_NUMBERS = ["3028938949"];
const TOLLFREE = /^(800|833|844|855|866|877|888)/;
// A vendor is judged by WHO the caller is — the caller-ID name, or "<Name>
// from/of <Company>" opening a voicemail — never by a lender a borrower
// MENTIONS ("has a Longbridge reverse mortgage" is a borrower).
const VENDOR_NAME = /fin\. of america|finance of america|longbridge|smartfi|propertyrate|class valuation|ringcentral|copier|mutual of omaha|\btitle\b|escrow|appraisal|accounting/i;
const VENDOR_CO = /^(?:the\s+)?(?:ring\s?central|arizona copier|copier|finance of america|fin\.? of america|longbridge|smart\s?fi|property\s?rate|class valuation|mutual of omaha)\b/i;
const VM_INTRO = /^\s*(?:(?:[Hh]i|[Hh]ello|[Hh]ey)[,!]?\s+)?(?:(?:[Tt]his is|[Ii]t'?s|[Ii]t is|[Mm]y name is)\s+)?[A-Z][A-Za-z'’.-]*(?:\s+[A-Z][A-Za-z'’.-]*){0,2},?\s+(?:from|of)\s+(.+)$/;
const VM_SELF_INTRO = /\b(?:[Tt]his is|[Mm]y name is)\s+[A-Z][A-Za-z'’.-]*(?:\s+[A-Z][A-Za-z'’.-]*){0,2},?\s+(?:from|of|with)\s+(.+)$/;
const VENDOR_VM_PHRASE = /account review/i;
const GENERIC = /^(unknown|wireless caller|anonymous|private(?: caller| number)?|restricted|unavailable|no caller id)$/i;
const US_STATES = "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(" ");
// "TULSA OK", "SAN JOSE CA" — a caller-ID city, not a person.
function isCityId(n) { const m = /^([A-Z .']+) ([A-Z]{2})$/.exec(String(n || "").trim()); return !!m && US_STATES.indexOf(m[2]) >= 0; }
const REMOVAL = /\bremov|no longer|inundated|do-not-(?:mail|contact)|\bdeletion\b|no further contact|spam-complaint|mail no longer be placed/i;
// "existing client" = has a reverse elsewhere → a response, not a file.
const FILE_STATUS = /active file|approval|cert & invoice|docs in progress/i;
// A call-only row is a working file when its voicemails show the loan moving.
const FILE_VM = /appraiser|appraisal fee|counseling meeting|counseling session|power-of-attorney/i;
const RETURNED_CALL = /\b(?:returned|returning|return(?:s|ing)?)\s+(?:reece'?s|your|his)\s+(?:call|message|voicemail)\b|\bcall(?:ed|ing) (?:reece|you) back\b/i;
// A borrower's voicemail on a lender's / vendor's line (a forwarded call: the
// caller ID is the lender's): it OPENS with a person's name ("Jo Example
// said…", "Robert called…") and asks about a reverse mortgage / the letter.
const VM_OPENS_NAME = /^([A-Z][a-z]+)(?:\s+[A-Z][a-z'’-]+)?,?\s+[a-z]/;
const BORROWER_VM = /\breverse mortgage\b|\b(?:the|your|a|his|reece'?s) (?:letter|mailer|postcard)\b/i;
const APPRAISAL_VM = /apprais/i;
// A caller-ID that names a business, not a person (vendors are already out).
const BUSINESS_ID = /\b(?:PROP|PROPS|PROPERTY|PROPERTIES|LLC|INC|CORP|CORPORATION|COMPANY|GROUP|SERVICES?|SVCS?|SOLUTIONS|REALTY|REALTORS?|REAL ESTATE|BANK|MORTGAGE|LENDING|LOANS?|FINANCIAL|INSURANCE|APPRAIS\w*|VALUATIONS?|INSPECT\w*|BUILDERS?|CONSTRUCTION|MANAGEMENT|MGMT|ASSOC\w*|AGENCY|PARTNERS|HOLDINGS|ENTERPRISES?|OFFICE|CLINIC|MEDICAL|HOSPITAL|PHARMACY|SCHOOL|CHURCH|COUNTY|DEPT|DEPARTMENT|ATTORNEYS?|CPA)\b/i;
// First words of a voicemail that aren't a first name ("[6/18] No
// transcription shown", "[x] The Smiths called").
const NOT_FIRST = new Set(["the", "this", "a", "an", "caller", "unknown", "someone", "brief", "no", "message", "voicemail", "reece", "mr", "mrs", "ms", "dr", "hi", "hello"]);
// Common first names — on an all-caps caller ID ("MICHAEL SHEEHAN" vs
// "SHEEHAN THOMAS") either word can be the last name; these aren't.
const COMMON_FIRST = new Set(("john james robert michael william david richard joseph thomas charles christopher daniel paul mark donald george kenneth steven edward brian ronald anthony kevin jason matthew gary timothy jose larry jeffrey frank scott eric stephen andrew raymond gregory joshua jerry dennis walter patrick peter harold douglas henry carl arthur ryan roger joe juan jack albert jonathan justin terry gerald keith samuel willie ralph lawrence nicholas roy benjamin bruce brandon adam harry fred wayne billy steve louis jeremy aaron randy howard eugene carlos russell bobby victor martin ernest phillip todd jesse craig alan shawn clarence sean philip chris johnny earl jimmy antonio danny bryan tony luis mike stanley leonard nathan dale manuel rodney curtis norman allen marvin vincent glenn jeff chad jacob lee melvin alfred kyle francis bradley herbert frederick ray joel edwin don eddie ricky troy randall barry alexander bernard mario leroy marcus theodore clifford jay jim tom calvin alex jon ronnie bill lloyd tommy leon derek warren darrell jerome floyd leo tim wesley gordon dean greg dan lewis herman vernon glen rick gene ben ron max bob dave ken " +
  "mary patricia linda barbara elizabeth jennifer maria susan margaret dorothy lisa nancy karen betty helen sandra donna carol ruth sharon michelle laura sarah kimberly deborah jessica shirley cynthia angela melissa brenda amy anna rebecca virginia kathleen pamela martha debra amanda stephanie carolyn christine marie janet catherine frances ann anne joyce diane alice julie heather teresa doris gloria evelyn jean cheryl mildred katherine joan judith rose janice kelly nicole judy christina kathy theresa beverly denise tammy irene jane lori rachel marilyn andrea kathryn louise sara jacqueline wanda bonnie julia ruby lois tina phyllis norma paula diana annie lillian emily robin peggy connie rita dawn edna carmen rosa cindy grace wendy victoria edith kim sherry sylvia josephine thelma sheila ethel ellen elaine marjorie carrie charlotte monica esther pauline emma juanita anita rhonda hazel eva debbie april leslie clara lucille joanne eleanor valerie gail darlene jill lynn sally dolores audrey yvonne annette june marion dana renee vivian roberta holly loretta beth jeanne sue").split(" "));
const NAME_NOISE = new Set(["and", "the", "trust", "trustee", "trustees", "estate", "living", "family", "revocable", "irrevocable", "mr", "mrs", "ms", "jr", "sr", "etal", "et", "al", "of", "rev", "tr", "ttee", "ttees", "life", "survivors", "survivor"]);

// Area code → state: a "likely" hint only (cell numbers travel), never a match.
const AREA_STATES = (function () {
  const m = new Map();
  ("AL:205 251 256 334 659 938|AK:907|AZ:480 520 602 623 928|AR:327 479 501 870|CA:209 213 279 310 323 341 350 369 408 415 424 442 510 530 559 562 619 626 628 650 657 661 669 707 714 747 760 805 818 820 831 840 858 909 916 925 949 951|CO:303 719 720 970 983|CT:203 475 860 959|DE:302|DC:202 771|FL:239 305 321 324 352 386 407 448 561 645 656 689 727 728 754 772 786 813 850 863 904 941 954|GA:229 404 470 478 678 706 762 770 912 943|HI:808|ID:208 986|IL:217 224 309 312 331 447 464 618 630 708 730 773 779 815 847 861 872|IN:219 260 317 463 574 765 812 930|IA:319 515 563 641 712|KS:316 620 785 913|KY:270 364 502 606 859|LA:225 318 337 504 985|ME:207|MD:227 240 301 410 443 667|MA:339 351 413 508 617 774 781 857 978|MI:231 248 269 313 517 586 616 679 734 810 906 947 989|MN:218 320 507 612 651 763 952|MS:228 601 662 769|MO:235 314 417 557 573 636 660 816 975|MT:406|NE:308 402 531|NV:702 725 775|NH:603|NJ:201 551 609 640 732 848 856 862 908 973|NM:505 575|NY:212 315 329 332 347 363 516 518 585 607 624 631 646 680 716 718 838 845 914 917 929 934|NC:252 336 472 704 743 828 910 919 980 984|ND:701|OH:216 220 234 283 326 330 380 419 436 440 513 567 614 740 937|OK:405 539 572 580 918|OR:458 503 541 971|PA:215 223 267 272 412 445 484 570 582 610 717 724 814 835 878|RI:401|SC:803 821 839 843 854 864|SD:605|TN:423 615 629 731 865 901 931|TX:210 214 254 281 325 346 361 409 430 432 469 512 682 713 726 737 806 817 830 832 903 915 936 940 945 956 972 979|UT:385 435 801|VT:802|VA:276 434 540 571 686 703 757 804 826 948|WA:206 253 360 425 509 564|WV:304 681|WI:262 274 353 414 534 608 715 920|WY:307")
    .split("|").forEach(function (part) { const st = part.slice(0, 2); part.slice(3).split(" ").forEach(function (a) { m.set(a, st); }); });
  return m;
})();
export function stateOfAreaCode(phone) { const d = d10(phone); return d.length === 10 ? AREA_STATES.get(d.slice(0, 3)) || "" : ""; }
// The same caller-ID / number rules, for the call-back list (src/callbacks.js
// hides these by default) — one copy of each rule, here.
export function isVendorCallerId(name) { return VENDOR_NAME.test(String(name == null ? "" : name)); }
export function isTollFreeNumber(phone) { const d = d10(phone); return d.length === 10 && TOLLFREE.test(d); }
export function isGenericCallerId(name) { return GENERIC.test(String(name == null ? "" : name).trim()); }

export const KINDS = ["response", "file", "removal"];
export const KIND_LABEL = { response: "Response", file: "Pipeline file", removal: "Removal request" };
export const MATCH_LABEL = { sure: "Sure", likely: "Likely — needs your click", confirmed: "Confirmed by you", none: "Not found" };

// ═══════ small helpers ═══════
export function d10(s) { const d = String(s == null ? "" : s).replace(/\D/g, ""); return d.length === 11 && d[0] === "1" ? d.slice(1) : d; }
function uniq(a) { const s = new Set(); const out = []; (a || []).forEach(function (x) { if (x != null && x !== "" && !s.has(x)) { s.add(x); out.push(x); } }); return out; }
function minDay(a) { let m = null; (a || []).forEach(function (d) { if (d && (!m || d < m)) m = d; }); return m; }
function maxDay(a) { let m = null; (a || []).forEach(function (d) { if (d && (!m || d > m)) m = d; }); return m; }
function pad2(n) { return String(n).padStart(2, "0"); }
function validDay(y, m, d) {
  y = +y; m = +m; d = +d;
  if (!(y >= 1900 && y <= 2200 && m >= 1 && m <= 12 && d >= 1 && d <= 31)) return null;
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCMonth() === m - 1 ? y + "-" + pad2(m) + "-" + pad2(d) : null;
}
// The export's own calendar day ("10/5/2026", "2026-06-27", "2026-06-27T…")
// — the date part as written, never shifted through a time zone.
export function dayOf(s) {
  s = String(s == null ? "" : s).trim(); let m;
  if ((m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s))) return validDay(m[1], m[2], m[3]);
  if ((m = /^(\d{1,2})\/(\d{1,2})\/(\d{4}|\d{2})\b/.exec(s))) return validDay(m[3].length === 2 ? 2000 + (+m[3]) : m[3], m[1], m[2]);
  return null;
}
export function fmtDay(day) { if (!day) return ""; const p = String(day).split("-"); return p.length === 3 ? (+p[1]) + "/" + (+p[2]) + "/" + p[0] : String(day); }
function vmSegments(vm) {
  return String(vm || "").split("||").map(function (s) { return s.trim(); }).filter(Boolean).map(function (seg) {
    const m = /^\[([^\]]+)\]\s*(.*)$/.exec(seg);
    return { day: m ? dayOf(m[1]) : null, text: m ? m[2] : seg, raw: seg };
  });
}
function vmDates(vm) { return vmSegments(vm).map(function (s) { return s.day; }).filter(Boolean); }
const vmNumbers = function (vm) { return Array.from(String(vm || "").matchAll(/\b(\d{3})-(\d{3})-(\d{4})\b/g)).map(function (m) { return m[1] + m[2] + m[3]; }); };
function cleanName(n) { return String(n || "").replace(/\s*-\s*Fwd to AI Ext\.?\s*\d+/ig, "").replace(/\(own number\)/i, "").trim(); }
const toks = function (s) { return String(s || "").toLowerCase().replace(/[^a-z ]/g, " ").split(/\s+/).filter(function (w) { return w.length >= 2; }); };
// Levenshtein ≤ 1 (the only distance any rule here asks about).
function within1(a, b) {
  if (a === b) return true;
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  let i = 0, j = 0, edits = 0;
  while (i < la && j < lb) {
    if (a[i] === b[j]) { i++; j++; continue; }
    if (++edits > 1) return false;
    if (la > lb) i++; else if (lb > la) j++; else { i++; j++; }
  }
  return edits + (la - i) + (lb - j) <= 1;
}
// A caller-ID text that is a phone number, a city, a placeholder — no name.
function isRealNameId(n) { return !!n && !GENERIC.test(n) && !isCityId(n) && !/^\(?\d{3}\)?[\s-]?\d{3}/.test(n) && !/^unknown \(/i.test(n); }

// ═══════ 1. CSV → rows ═══════
// Quoted fields with commas / newlines / doubled quotes, CRLF or LF, a BOM,
// and a ; or tab delimiter when the header line has no commas.
export function parseCsv(text) {
  let t = String(text == null ? "" : text);
  if (t.charCodeAt(0) === 0xFEFF) t = t.slice(1);
  const nl = t.search(/\r?\n/);
  const head = nl < 0 ? t : t.slice(0, nl);
  const count = function (ch) { let n = 0, q = false; for (let i = 0; i < head.length; i++) { if (head[i] === '"') q = !q; else if (!q && head[i] === ch) n++; } return n; };
  const c = count(","), tb = count("\t"), sc = count(";");
  const delim = c >= tb && c >= sc ? "," : tb >= sc ? "\t" : ";";
  const rows = []; let row = [], f = "", q = false;
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (q) { if (ch === '"') { if (t[i + 1] === '"') { f += '"'; i++; } else q = false; } else f += ch; continue; }
    if (ch === '"' && f === "") { q = true; continue; }
    if (ch === delim) { row.push(f); f = ""; continue; }
    if (ch === "\r" || ch === "\n") { if (ch === "\r" && t[i + 1] === "\n") i++; row.push(f); rows.push(row); row = []; f = ""; continue; }
    f += ch;
  }
  if (f !== "" || row.length) { row.push(f); rows.push(row); }
  return rows;
}
export function normHead(h) { return String(h == null ? "" : h).replace(/^﻿/, "").trim().toLowerCase().replace(/[_\-./]+/g, " ").replace(/\s+/g, " ").trim(); }
// Field → accepted header names (normalized), exact match only, in order.
const COLS = {
  name: ["name", "contact name", "caller name", "caller", "caller id", "display name"],
  altNames: ["all names seen", "names seen", "other names"],
  phone: ["phone number", "phone", "number", "caller number", "from number"],
  altPhones: ["alt phones", "alternate phones", "other phones", "alt phone"],
  email: ["contact email", "email", "email address", "emails"],
  loanNo: ["loan or app number", "loan number", "app number", "application number", "loan app number"],
  address: ["property address", "address"],
  first: ["first contact", "first call", "first seen"],
  last: ["last contact", "last call", "last seen"],
  inbound: ["inbound", "inbound calls"],
  outbound: ["outbound", "outbound calls"],
  missed: ["missed", "missed calls"],
  voicemails: ["voicemails", "voicemail count"],
  vm: ["voicemail summaries", "voicemail summary", "voicemail transcripts", "voicemail text"],
  emailMsgs: ["email messages", "emails received", "email count"],
  emailFirst: ["email first seen"],
  emailLast: ["email last seen"],
  emailStatus: ["email status"],
  notes: ["notes", "note"],
  basis: ["match basis"],
  source: ["source"],
};
export function contactColumns(headers) {
  const h = (headers || []).map(normHead);
  const cols = {};
  Object.keys(COLS).forEach(function (k) {
    cols[k] = -1;
    for (let i = 0; i < COLS[k].length; i++) { const j = h.indexOf(COLS[k][i]); if (j >= 0) { cols[k] = j; break; } }
  });
  const hasWho = cols.phone >= 0 || cols.email >= 0;
  const hasWhat = cols.inbound >= 0 || cols.missed >= 0 || cols.voicemails >= 0 || cols.vm >= 0 || cols.emailMsgs >= 0;
  if (!hasWho || !hasWhat) return null;
  cols.format = cols.email >= 0 || cols.emailMsgs >= 0 ? "unified" : "callLog";
  return cols;
}
export const FORMAT_LABEL = { unified: "Call & email log", callLog: "Call log" };
// One export → {ok, format, rows, error}. Rows are normalized (see rowFrom).
export function readContactCsv(text, fileName) {
  const grid = parseCsv(text).filter(function (r) { return r.some(function (x) { return String(x).trim() !== ""; }); });
  if (!grid.length) return { ok: false, error: (fileName || "That file") + " is empty.", rows: [] };
  const cols = contactColumns(grid[0]);
  if (!cols) return { ok: false, error: (fileName || "That file") + " doesn't look like a call or email log — it needs a phone or email column and the calls / voicemails / email counts (RingCentral's call_log_by_number.csv or unified_contacts.csv).", rows: [] };
  const rows = grid.slice(1).map(function (cells, i) { return rowFrom(cells, cols, i, fileName || "log"); });
  return { ok: true, format: cols.format, rows: rows, error: null };
}
function rowFrom(cells, cols, i, file) {
  const get = function (k) { return cols[k] >= 0 ? String(cells[cols[k]] == null ? "" : cells[cols[k]]).trim() : ""; };
  const num = function (k) { const n = parseFloat(get(k).replace(/[^0-9.-]/g, "")); return Number.isFinite(n) && n > 0 ? n : 0; };
  const notes = get("notes");
  const seen = /all names seen:\s*(.+)$/i.exec(notes);
  const alt = (get("altNames") || (seen ? seen[1] : "")).split(/\s*\/\s*/).map(cleanName).filter(Boolean);
  const name = cleanName(get("name"));
  return {
    i: i, file: file, line: i + 2,
    name: name,
    altNames: uniq(alt.filter(function (n) { return n !== name; })),
    phones: uniq([d10(get("phone"))].concat(get("altPhones").split(/[;,]/).map(d10))).filter(function (p) { return p.length === 10; }),
    emails: uniq(get("email").toLowerCase().split(/[;,\s]+/).filter(function (e) { return /@/.test(e); })),
    loanNo: get("loanNo"), address: get("address"),
    first: get("first"), last: get("last"),
    inbound: num("inbound"), outbound: num("outbound"), missed: num("missed"), voicemails: num("voicemails"),
    vm: get("vm"), emailMsgs: num("emailMsgs"), emailFirst: get("emailFirst"), emailLast: get("emailLast"),
    emailStatus: get("emailStatus"), notes: notes, basis: get("basis"), source: get("source") || (cols.format === "callLog" ? "call_log" : ""),
  };
}

// ═══════ 2. classify rows ═══════
function vendorIntro(text) {
  const a = VM_INTRO.exec(text); if (a && VENDOR_CO.test(a[1])) return true;
  const b = VM_SELF_INTRO.exec(text); return !!(b && VENDOR_CO.test(b[1]));
}
function vendorByVoicemail(vm) {
  if (VENDOR_VM_PHRASE.test(vm)) return true;
  return vmSegments(vm).some(function (s) { return vendorIntro(s.text); });
}
// The voicemails on a vendor row that sound like a BORROWER: they open with
// a person's name (not "<Name> from/of/with <Company>"), and ask about a
// reverse mortgage / the letter. Each one becomes its own row (vendorVmRow).
function borrowerVoicemails(r) {
  return vmSegments(r.vm).filter(function (s) {
    const m = VM_OPENS_NAME.exec(s.text);
    if (!m || NOT_FIRST.has(m[1].toLowerCase())) return false;
    if (VENDOR_VM_PHRASE.test(s.text) || vendorIntro(s.text)) return false;
    const a = VM_INTRO.exec(s.text) || VM_SELF_INTRO.exec(s.text);
    if (a && VENDOR_NAME.test(a[1])) return false;
    return BORROWER_VM.test(s.text);
  });
}
// One borrower voicemail off a vendor row → a row of its own. The vendor's
// number isn't theirs; the callback number the voicemail gives IS (it joins
// a row of the log with that phone — e.g. one he only called out to). With no
// number at all, a stable reference keeps a re-import from adding them twice.
function vendorVmRow(r, s, self) {
  const via = [r.name].concat(r.altNames).find(function (n) { return VENDOR_NAME.test(n); }) || r.name || "a vendor";
  const phones = uniq(vmNumbers(s.raw).filter(function (p) { return !self.has(p) && !TOLLFREE.test(p); }));
  return {
    i: r.i, file: r.file, line: r.line, name: "", altNames: [], phones: phones, emails: [], loanNo: "", address: "",
    first: s.day || "", last: s.day || "", inbound: 0, outbound: 0, missed: 0, voicemails: 1, vm: s.raw,
    emailMsgs: 0, emailFirst: "", emailLast: "", emailStatus: "", notes: "", basis: "", source: r.source,
    _viaVendor: via,
    _ref: phones.length ? "" : "vm:" + (r.phones[0] || "") + ":" + (s.day || "") + ":" + toks(s.text).slice(0, 6).join(" "),
  };
}
export function selfSet(extra) {
  const s = new Set(OWN_NUMBERS);
  (extra || []).forEach(function (p) { const d = d10(p); if (d.length === 10) s.add(d); });
  return s;
}
// Row classes: self · business · spam · vendor · tollfree · internal ·
// outbound-only ·
// contact. Only "contact" rows make someone a responder.
function classifyRow(r, self) {
  r._calledIn = r.inbound + r.missed + r.voicemails > 0;
  r._emailed = r.emailMsgs > 0;
  r._vmNums = vmNumbers(r.vm);
  const ids = [r.name].concat(r.altNames);
  if (/self/i.test(r.basis) || r.phones.some(function (p) { return self.has(p); })) return "self";
  if (/^business$/i.test(r.emailStatus)) return "business";
  if (/^spam$/i.test(r.emailStatus)) return "spam";
  if (ids.some(function (n) { return VENDOR_NAME.test(n); }) || vendorByVoicemail(r.vm)) return "vendor";
  if (r.phones.length && TOLLFREE.test(r.phones[0]) && !r._emailed) return "tollfree";
  // No 10-digit number and no email: an extension / internal call — nobody
  // to count (the call log lists his own extension calls this way).
  if (!r.phones.length && !r.emails.length) return "internal";
  if (!r._calledIn && !r._emailed) return "outbound-only";
  return "contact";
}
// Two-word person names on a row: the caller ID ("LAST FIRST", "LAST,FIRST",
// "First Last", "A / B", "A & B") + the names voicemails open with.
function namesOf(r) {
  const out = [], shown = [], vmShown = [];
  const n = r.name;
  if (isRealNameId(n)) {
    n.split(/\s*\/\s*|\s*&\s*/).forEach(function (part) { const t = toks(part.replace(",", " ")); if (t.length >= 2) { out.push([t[0], t[1]]); shown.push(part.trim()); } });
  }
  vmSegments(r.vm).forEach(function (s) {
    const m = /^([A-Z][a-z]+)\s+([A-Z][a-z'’-]{2,})(?:\s|,|$)/.exec(s.text);
    if (m && !NOT_FIRST.has(m[1].toLowerCase())) { out.push([m[1].toLowerCase(), m[2].toLowerCase().replace(/['’-]/g, "")]); vmShown.push(m[1] + " " + m[2]); }
  });
  return { pairs: out, shown: shown, vmShown: vmShown };
}
// Same person if one word is identical (≥3 letters) and the other identical
// or one letter off (both ≥5 letters) — names in either order.
export function sameName(a, b) {
  const P = [[0, 0], [0, 1], [1, 0], [1, 1]];
  for (let k = 0; k < 4; k++) {
    const x = P[k][0], y = P[k][1];
    const p = a[x], q = b[y], p2 = a[1 - x], q2 = b[1 - y];
    if (p === q && p.length >= 3 && (p2 === q2 || (p2.length >= 5 && q2.length >= 5 && within1(p2, q2)))) return true;
  }
  return false;
}
function rowDates(r) {
  const call = [], email = [], last = [];
  const combined = /call/i.test(r.source) && /mail/i.test(r.source);
  const vd = vmDates(r.vm);
  if (r._calledIn) {
    const f = dayOf(r.first);
    // merged call + email rows: first_contact holds the EMAIL date; every
    // call happened on/before last_contact
    if (f) (combined ? email : call).push(f);
    if (combined) { const l = dayOf(r.last); if (l) call.push(l); }
  }
  if (r._emailed) { const f = dayOf(r.emailFirst); if (f) email.push(f); }
  vd.forEach(function (d) { call.push(d); });
  if (r._calledIn || r._emailed) { const l = dayOf(r.last); if (l) last.push(l); }
  if (r._emailed) { const l = dayOf(r.emailLast); if (l) last.push(l); }
  vd.forEach(function (d) { last.push(d); });
  r._firstCall = minDay(call); r._firstEmail = minDay(email);
  r._first = minDay(call.concat(email));
  r._last = maxDay(last.concat(call, email));
}

// ═══════ 3. rows → people ═══════
// opts.selfPhones — his own numbers on top of OWN_NUMBERS.
// → {people (responders only), outboundOnly, rows, byClass, merges,
//    vendorLine (borrower voicemails kept off vendor rows)}
export function buildPeople(rows, opts) {
  const o = opts || {};
  const self = selfSet(o.selfPhones);
  const all = (rows || []).filter(Boolean);
  const prep = function (r) {
    r._removal = REMOVAL.test(r.vm) || REMOVAL.test(r.emailStatus) || REMOVAL.test(r.notes);
    r._file = FILE_STATUS.test(r.emailStatus) || !!r.loanNo || FILE_VM.test(r.vm);
    const nm = namesOf(r);
    r._names = nm.pairs; r._shown = nm.shown; r._vmShown = nm.vmShown;
    rowDates(r);
  };
  all.forEach(function (r, i) { r._idx = i; r._cls = classifyRow(r, self); prep(r); });
  const byClass = {};
  all.forEach(function (r) { byClass[r._cls] = (byClass[r._cls] || 0) + 1; });
  // A vendor row stays out, but a borrower's voicemail on it is a row of its
  // own (a contact) — and the person goes under Check these.
  const work = all.slice();
  all.forEach(function (r) {
    if (r._cls !== "vendor") return;
    borrowerVoicemails(r).forEach(function (s) {
      const d = vendorVmRow(r, s, self);
      d._idx = work.length; d._cls = "contact"; d._calledIn = true; d._emailed = false; d._vmNums = vmNumbers(d.vm);
      prep(d);
      work.push(d);
    });
  });
  // ── union-find ──
  const par = work.map(function (_, i) { return i; });
  const find = function (i) { while (par[i] !== i) { par[i] = par[par[i]]; i = par[i]; } return i; };
  const merges = {};
  const union = function (a, b, why) { const x = find(a), y = find(b); if (x !== y) { par[y] = x; merges[why] = (merges[why] || 0) + 1; } };
  const SKIP = { self: 1, vendor: 1, business: 1, spam: 1, tollfree: 1, internal: 1 };
  const live = work.filter(function (r) { return !SKIP[r._cls]; });
  const byKey = new Map();
  const link = function (key, r, why) { if (byKey.has(key)) union(byKey.get(key), r._idx, why); else byKey.set(key, r._idx); };
  live.forEach(function (r) {
    r.phones.forEach(function (p) { link("p" + p, r, "same phone"); });
    r.emails.forEach(function (e) { link("e" + e, r, "same email"); });
    if (r.loanNo) link("l" + r.loanNo, r, "same loan no.");
    if (r._ref) link("r" + r._ref, r, "same voicemail");
  });
  live.forEach(function (r) { r._vmNums.forEach(function (p) { if (byKey.has("p" + p)) union(byKey.get("p" + p), r._idx, "callback number in voicemail"); }); });
  // Names: only rows sharing an identical word (≥3 letters) can be the same.
  const bucket = new Map();
  live.forEach(function (r) {
    const seen = new Set();
    r._names.forEach(function (pr) { pr.forEach(function (t) { if (t.length >= 3 && !seen.has(t)) { seen.add(t); if (!bucket.has(t)) bucket.set(t, []); bucket.get(t).push(r); } }); });
  });
  const checked = new Set();
  bucket.forEach(function (rs) {
    for (let i = 0; i < rs.length; i++) for (let j = i + 1; j < rs.length; j++) {
      const a = rs[i], b = rs[j];
      const k = a._idx < b._idx ? a._idx + "," + b._idx : b._idx + "," + a._idx;
      if (checked.has(k)) continue;
      checked.add(k);
      if (a._names.some(function (x) { return b._names.some(function (y) { return sameName(x, y); }); })) union(a._idx, b._idx, "same name");
    }
  });
  // "Per Steve's Request — connected to Steve Rondel": someone writing on
  // another person's behalf is that person's thread.
  live.forEach(function (r) {
    const m = /connected to ([A-Z][a-z]+) ([A-Z][a-z]+)/.exec(String(r.notes || "")); if (!m) return;
    const key = [m[1].toLowerCase(), m[2].toLowerCase()];
    const cands = new Set((bucket.get(key[0]) || []).concat(bucket.get(key[1]) || []));
    cands.forEach(function (o2) { if (o2 !== r && o2._names.some(function (x) { return sameName(x, key); })) union(o2._idx, r._idx, "notes say connected to them"); });
  });
  // "Unknown (dondelaney6)": an email local part = first name / initial + a
  // last name (≥5 letters) of someone else in the log.
  const lastIdx = new Map();
  live.forEach(function (o2) {
    o2._names.forEach(function (pr) {
      [[pr[0], pr[1]], [pr[1], pr[0]]].forEach(function (fl) {
        if (fl[1].length < 5) return;
        if (!lastIdx.has(fl[1])) lastIdx.set(fl[1], []);
        lastIdx.get(fl[1]).push({ first: fl[0], row: o2 });
      });
    });
  });
  live.forEach(function (r) {
    if (r._names.length) return;
    r.emails.forEach(function (e) {
      const lp = e.split("@")[0].replace(/[^a-z]/g, "");
      const hit = new Set();
      for (let a = 0; a < lp.length; a++) for (let b = a + 5; b <= lp.length; b++) {
        const sub = lp.slice(a, b);
        if (hit.has(sub)) continue; hit.add(sub);
        (lastIdx.get(sub) || []).forEach(function (x) { if (x.row !== r && lp[0] === x.first[0]) union(x.row._idx, r._idx, "email address = their name"); });
      }
    });
  });
  // ── people ──
  const groups = new Map();
  live.forEach(function (r) { const k = find(r._idx); if (!groups.has(k)) groups.set(k, []); groups.get(k).push(r); });
  const people = [];
  let outboundOnly = 0;
  groups.forEach(function (rs) {
    const p = personFrom(rs, self);
    if (p) people.push(p); else outboundOnly++;
  });
  return { people: people, outboundOnly: outboundOnly, rows: all.length, byClass: byClass, merges: merges, vendorLine: work.length - all.length };
}

function snippet(s, n) { s = String(s || "").replace(/\s+/g, " ").trim(); return s.length > (n || 240) ? s.slice(0, (n || 240) - 1) + "…" : s; }
// One group of rows → a responder, or null (nobody in it called in or emailed).
function personFrom(rs, self) {
  const contact = rs.filter(function (r) { return r._cls === "contact"; });
  if (!contact.length) return null;
  const called = rs.some(function (r) { return r._calledIn; }), emailed = rs.some(function (r) { return r._emailed; });
  const firstAt = minDay(contact.map(function (r) { return r._first; }));
  const firstCallAt = minDay(contact.map(function (r) { return r._firstCall; }));
  const firstEmailAt = minDay(contact.map(function (r) { return r._firstEmail; }));
  const lastAt = maxDay(rs.map(function (r) { return r._last; }).concat([firstAt]));
  const anyFile = rs.some(function (r) { return r._file; }), anyRemoval = rs.some(function (r) { return r._removal; });
  const kind = anyRemoval && !anyFile ? "removal" : anyFile ? "file" : "response";
  const reasons = [];
  let said = "";
  const segs = [];
  rs.forEach(function (r) { vmSegments(r.vm).forEach(function (s) { segs.push(s); }); });
  segs.sort(function (a, b) { return (a.day || "9999") < (b.day || "9999") ? -1 : (a.day || "9999") > (b.day || "9999") ? 1 : 0; });
  if (kind === "removal") {
    rs.forEach(function (r) {
      if (!r._removal) return;
      const seg = vmSegments(r.vm).find(function (s) { return REMOVAL.test(s.text); });
      if (seg) { reasons.push("Asked to be removed (voicemail" + (seg.day ? " " + fmtDay(seg.day) : "") + ")"); if (!said) said = seg.raw; }
      if (REMOVAL.test(r.emailStatus)) { reasons.push("Email status: " + r.emailStatus); if (!said) said = "Email status: " + r.emailStatus + (r.notes ? " — " + r.notes : ""); }
      if (REMOVAL.test(r.notes)) { reasons.push("Asked to be removed (email)"); if (!said) said = r.notes; }
    });
  } else if (kind === "file") {
    rs.forEach(function (r) {
      if (FILE_STATUS.test(r.emailStatus)) reasons.push("Email status: " + r.emailStatus);
      if (r.loanNo) reasons.push("Loan / application number on the log");
      const fm = FILE_VM.exec(r.vm); if (fm) reasons.push("Voicemail mentions “" + fm[0] + "”");
    });
  }
  if (called) { const n = rs.reduce(function (s, r) { return s + r.inbound + r.missed; }, 0), v = rs.reduce(function (s, r) { return s + r.voicemails; }, 0); reasons.push("Called in" + (n ? " (" + n + " call" + (n === 1 ? "" : "s") + (v ? ", " + v + " voicemail" + (v === 1 ? "" : "s") : "") + ")" : "")); }
  if (emailed) { const n = rs.reduce(function (s, r) { return s + r.emailMsgs; }, 0); reasons.push("Emailed (" + n + " message" + (n === 1 ? "" : "s") + ")"); }
  if (rs.some(function (r) { return /existing client/i.test(r.emailStatus); })) reasons.push("Has a reverse mortgage elsewhere (“existing client”) — counted as a response");
  if (!said) said = segs.length ? segs[0].raw : (rs.find(function (r) { return r.notes && !/^all names seen:/i.test(r.notes); }) || {}).notes || "";
  // Places a likely match can lean on: area codes, a city caller ID, an
  // address said in a voicemail or on the email thread.
  const phones = uniq([].concat.apply([], rs.map(function (r) { return r.phones; })));
  // A number said in a voicemail that is NOT a row of this person (a callback
  // that isn't another row's phone — it could be a counselor, an office, a
  // relative): matched against the CRM, never a key (two callers quoting the
  // same office number are still two people).
  const cb = uniq([].concat.apply([], rs.map(function (r) { return r._vmNums; }))).filter(function (p) { return p.length === 10 && !self.has(p) && !TOLLFREE.test(p) && phones.indexOf(p) < 0; });
  const places = placesOf(rs, phones.concat(cb));
  // Every contact row came in on a vendor's line (vendorVmRow) → Check these.
  const viaVendor = contact.every(function (r) { return r._viaVendor; }) ? uniq(contact.map(function (r) { return r._viaVendor; })) : [];
  const ids = uniq([].concat.apply([], rs.map(function (r) { return [r.name].concat(r.altNames); })).filter(Boolean)).slice(0, 8);
  const shown = uniq([].concat.apply([], rs.map(function (r) { return r._shown; })));
  const vmNames = uniq([].concat.apply([], rs.map(function (r) { return r._vmShown; })));
  const first = segs[0];
  const appr = uniq(segs.filter(function (s) { return s.day && APPRAISAL_VM.test(s.text); }).map(function (s) { return s.day; }));
  return {
    phones: phones,
    callbackPhones: cb,
    emails: uniq([].concat.apply([], rs.map(function (r) { return r.emails; }))),
    refs: uniq(rs.map(function (r) { return r._ref; })),
    loanNos: uniq(rs.map(function (r) { return r.loanNo; })),
    names: uniq(shown.concat(vmNames)),
    firstAt: firstAt, lastAt: lastAt, firstCallAt: firstCallAt, firstEmailAt: firstEmailAt,
    method: called && emailed ? "Call + Email" : called ? "Call" : "Email",
    firstMethod: firstEmailAt && (!firstCallAt || firstEmailAt < firstCallAt) ? "Email" : called ? "Call" : "Email",
    kind: kind,
    reasons: uniq(reasons),
    said: snippet(said),
    sources: uniq(rs.map(function (r) { return r.file; })),
    rows: rs.length,
    facts: {
      ids: ids,
      vmNames: vmNames,
      vm: rs.reduce(function (s, r) { return s + r.voicemails; }, 0) || segs.length,
      answered: rs.reduce(function (s, r) { return s + r.inbound; }, 0),
      missed: rs.reduce(function (s, r) { return s + r.missed; }, 0),
      statuses: uniq(rs.map(function (r) { return r.emailStatus; })),
      returned: !!(first && RETURNED_CALL.test(first.text)),
      apprDays: appr,
      places: places,
      viaVendor: viaVendor,
    },
  };
}
const STREET_RE = /\b(\d{2,6})\s+(?:(?:N|S|E|W|NE|NW|SE|SW|North|South|East|West)\.?\s+)?([A-Za-z][A-Za-z']{2,})/g;
function placesOf(rs, phones) {
  const states = [], cities = [], streets = [];
  phones.forEach(function (p) { const s = stateOfAreaCode(p); if (s) states.push(s); });
  rs.forEach(function (r) {
    [r.name].concat(r.altNames).forEach(function (n) { const m = /^([A-Z .']+) ([A-Z]{2})$/.exec(n || ""); if (m && isCityId(n)) { cities.push(m[1].trim().toLowerCase()); states.push(m[2]); } });
    const texts = [r.address].concat(vmSegments(r.vm).map(function (s) { return s.text; }));
    texts.forEach(function (tx) {
      if (!tx) return;
      Array.from(String(tx).matchAll(STREET_RE)).forEach(function (m) { if (!/^(am|pm|and|the|calls?|minutes?|seconds?|years?|days?|weeks?|months?)$/i.test(m[2])) streets.push(m[1] + " " + m[2].toLowerCase()); });
      // "(2489 Ramona Street, Palo Alto CA 94301)", "… 4085 South Dexter, Denver, CO"
      const parts = String(tx).split(/[();]|\band\b/);
      parts.forEach(function (part) { if (!/\d/.test(part)) return; const pl = placeFromAddress(part.replace(/[.\s]+$/, "")); if (pl && pl.state) { states.push(pl.state); if (pl.city) cities.push(pl.city.toLowerCase()); } });
    });
  });
  return { states: uniq(states), cities: uniq(cities), streets: uniq(streets) };
}

// ═══════ 4. "Check these" — the calls the rules can't make ═══════
// Every flag is a GENERIC rule (no names). A flagged person still counts
// until Reece decides: not a response / same person as … / keep.
//   business  a caller ID that names a business (+ "the day of an appraisal
//             on another file" when a voicemail on another person mentions
//             one that day)
//   surname   another person with the same last name in the same area code
//   cluster   one of several same-day missed calls whose caller IDs only
//             name a city, no voicemail, nobody answered, no email
//   noname    an email-only person with no name and no status
//   returned  their first voicemail says they were returning Reece's call
//   vendorline  they only came in on a vendor's / lender's line (the caller
//             ID is the vendor's), but the voicemail opens with a person's
//             name about a reverse mortgage / the letter
export const FLAG_LABEL = { business: "Business-looking caller ID", surname: "Same last name, same area code", cluster: "Same-day missed calls, city-only caller IDs", noname: "Email with no name", returned: "Returning Reece's call", vendorline: "Borrower voicemail on a vendor's line" };
export const CLUSTER_MIN = 2;
function surnamesOf(p) {
  const out = new Set();
  const f = p.facts || {};
  (f.vmNames || []).forEach(function (n) { const t = toks(n); if (t.length >= 2 && t[1].length >= 4) out.add(t[1]); });
  (f.ids || []).forEach(function (n) {
    if (!isRealNameId(n)) return;
    n.split(/\s*\/\s*|\s*&\s*/).forEach(function (part) {
      const comma = part.indexOf(",") >= 0;
      const t = toks(part.replace(",", " ")); if (t.length < 2) return;
      if (comma) { if (t[0].length >= 4) out.add(t[0]); return; }
      if (part !== part.toUpperCase()) { if (t[t.length - 1].length >= 4) out.add(t[t.length - 1]); return; }
      t.slice(0, 2).forEach(function (w) { if (w.length >= 4 && !COMMON_FIRST.has(w)) out.add(w); });
    });
  });
  return Array.from(out);
}
function cityOnlyPerson(p) {
  const f = p.facts || {};
  return (f.ids || []).length > 0 && (f.ids || []).every(function (n) { return !isRealNameId(n); }) && !(f.vmNames || []).length && (f.ids || []).some(isCityId);
}
// people → Map(id → [{key, text, other?}]) — over the active people only.
export function checkFlags(people) {
  const act = (people || []).filter(isActive);
  const out = new Map();
  const add = function (p, f) { if (!out.has(p.id)) out.set(p.id, []); if (!out.get(p.id).some(function (x) { return x.key === f.key; })) out.get(p.id).push(f); };
  const apprBy = new Map();
  act.forEach(function (p) { ((p.facts && p.facts.apprDays) || []).forEach(function (d) { if (!apprBy.has(d)) apprBy.set(d, []); apprBy.get(d).push(p.id); }); });
  const clusters = new Map();
  const bySurArea = new Map();
  act.forEach(function (p) {
    const f = p.facts || {};
    const biz = (f.ids || []).find(function (n) { return BUSINESS_ID.test(n); });
    if (biz) {
      const d = p.firstCallAt || p.firstAt;
      const other = d ? (apprBy.get(d) || []).filter(function (x) { return x !== p.id; }) : [];
      add(p, { key: "business", text: "Caller ID looks like a business (“" + biz + "”)" + (other.length ? " — it called on " + fmtDay(d) + ", the day of an appraisal on another file" : "") + ". A borrower, or someone working a file?", other: other[0] || null });
    }
    if (cityOnlyPerson(p) && !f.vm && !f.answered && p.method === "Call" && p.firstCallAt) {
      if (!clusters.has(p.firstCallAt)) clusters.set(p.firstCallAt, []);
      clusters.get(p.firstCallAt).push(p);
    }
    if (p.method === "Email" && !(p.names || []).length && (f.statuses || []).every(function (s) { return !s || /^unknown$/i.test(s); })) {
      add(p, { key: "noname", text: "An email with no name on it" + (p.said ? " (“" + snippet(p.said, 90) + "”)" : "") + " — a borrower?" });
    }
    if (f.returned) add(p, { key: "returned", text: "Their first voicemail says they were returning Reece's call — was this a response to the mail?" });
    if ((f.viaVendor || []).length) add(p, { key: "vendorline", text: "Came in on a vendor's line (caller ID “" + f.viaVendor[0] + "”), but the voicemail opens with a person's name and asks about a reverse mortgage — a borrower?" });
    const areas = uniq((p.phones || []).map(function (x) { return x.slice(0, 3); }));
    surnamesOf(p).forEach(function (s) { areas.forEach(function (a) { const k = s + "|" + a; if (!bySurArea.has(k)) bySurArea.set(k, []); if (bySurArea.get(k).indexOf(p) < 0) bySurArea.get(k).push(p); }); });
  });
  clusters.forEach(function (ps, day) {
    if (ps.length < CLUSTER_MIN) return;
    ps.forEach(function (p) { add(p, { key: "cluster", text: "One of " + ps.length + " missed calls on " + fmtDay(day) + " whose caller IDs only name a city — no voicemail, nobody answered. Robocalls?" }); });
  });
  bySurArea.forEach(function (ps, k) {
    if (ps.length < 2) return;
    const parts = k.split("|"), sur = parts[0], area = parts[1];
    ps.forEach(function (p) {
      const o = ps.find(function (x) { return x !== p; });
      add(p, { key: "surname", text: "Same last name (“" + sur.charAt(0).toUpperCase() + sur.slice(1) + "”) and area code (" + area + ") as " + personLabel(o) + " — the same household?", other: o.id });
    });
  });
  return out;
}
// The flags he hasn't decided yet.
export function openFlags(p, flags) {
  const list = (flags && flags.get ? flags.get(p.id) : null) || [];
  const dec = (p.decisions && p.decisions.check) || {};
  return list.filter(function (f) { return !dec[f.key]; });
}
export function personLabel(p) {
  if (!p) return "someone";
  const n = (p.names || [])[0];
  if (n) return n;
  const ph = (p.phones || [])[0];
  if (ph) return "(" + ph.slice(0, 3) + ") " + ph.slice(3, 6) + "-" + ph.slice(6);
  return (p.emails || [])[0] || "someone";
}

// ═══════ 5. the people store's shape + decisions ═══════
// Person (app shape): {id, phones, callbackPhones, emails, refs, loanNos,
// names, firstAt, lastAt, firstCallAt, firstEmailAt, method, firstMethod,
// kind, reasons, said, sources, rows, facts, segment, leadId, loanId, match,
// likelyLeadIds, decisions, createdAt, updatedAt}
//   keys (one person per key): phones, emails, refs. callbackPhones (numbers
//   only ever SAID in a voicemail) are matched against the CRM, never a key.
//   decisions: {check:{flagKey:"keep"|"not"}, notResponse, mergedInto,
//               mergedBy:"you", absorbed:[ids], unmerge:{childId:{decKeys,
//               checkKeys}}, own:{evidence}, pick:{leadId, loanId}|{none:true},
//               dnc:{leadId, at}, marked:{leadId, loanId, at, day}}
//   (unmerge / own: the people he merged in by hand — see "Same person as …")
export function isActive(p) { return !!p && !(p.decisions && p.decisions.mergedInto); }
export function counts(p) { return isActive(p) && !(p.decisions && p.decisions.notResponse); }
function rand36(n) {
  let s = "";
  try {
    const c = typeof window !== "undefined" ? window.crypto : null;
    if (c && typeof c.getRandomValues === "function") { const a = new Uint32Array(n); c.getRandomValues(a); for (let i = 0; i < n; i++) s += (a[i] % 36).toString(36); return s; }
  } catch (e) { /* fall back below */ }
  while (s.length < n) s += Math.random().toString(36).slice(2);
  return s.slice(0, n);
}
// "CR-<time36>-<random36>" — never length-based.
export function newPersonId(taken) {
  for (let i = 0; i < 20; i++) { const id = "CR-" + Date.now().toString(36) + "-" + rand36(8); if (typeof taken !== "function" || !taken(id)) return id; }
  return "CR-" + Date.now().toString(36) + "-" + rand36(16);
}
const msOf = function (v) { const t = Date.parse(v); return Number.isFinite(t) ? t : 0; };
function stampAfter(prevIso) { return new Date(Math.max(Date.now(), msOf(prevIso) + 1)).toISOString(); }
const KIND_RANK = { response: 1, removal: 2, file: 3 };
function strongerKind(a, b) { return (KIND_RANK[b] || 0) > (KIND_RANK[a] || 0) ? b : a; }
function methodOf(called, emailed) { return called && emailed ? "Call + Email" : called ? "Call" : emailed ? "Email" : ""; }
const hasCall = function (m) { return /call/i.test(m || ""); };
const hasEmail = function (m) { return /email/i.test(m || ""); };
// Fold person b's evidence into a (keys, names, dates, kind…). Decisions are
// a's, plus b's flag decisions a doesn't have.
export function foldPerson(a, b) {
  const firstCallAt = minDay([a.firstCallAt, b.firstCallAt]), firstEmailAt = minDay([a.firstEmailAt, b.firstEmailAt]);
  const fa = a.facts || {}, fb = b.facts || {};
  const pa = fa.places || {}, pb = fb.places || {};
  const firstAt = minDay([a.firstAt, b.firstAt]);
  const said = a.kind === "removal" || !(b.kind === "removal") ? (a.said || b.said) : (b.said || a.said);
  const da = a.decisions || {}, db = b.decisions || {};
  // a's decisions, plus the ones only b has (an unchanged person keeps the
  // exact same decisions — a re-import of the same file changes nothing)
  const decisions = Object.assign({}, da);
  const check = Object.assign({}, db.check || {}, da.check || {});
  if (Object.keys(check).length) decisions.check = check;
  // b's pick / "not a response" come along when a has none; a Do-not-contact
  // or "marked" record only when both point at the same lead — never shown as
  // done for a lead it wasn't made for (review H)
  const sa = Object.assign({}, da.stamps || {}), sb = db.stamps || {};
  const sameLead = (a.leadId || null) === (b.leadId || null);
  ["pick", "dnc", "marked", "notResponse"].forEach(function (k) {
    if (k === "notResponse" ? (da[k] !== undefined || db[k] !== true) : (da[k] || !db[k])) return;
    if ((k === "dnc" || k === "marked") && !sameLead) return;
    decisions[k] = db[k];
    if (sb[k]) sa[k] = sb[k];
  });
  if (Object.keys(sa).length) decisions.stamps = sa;
  const phones = uniq((a.phones || []).concat(b.phones || []));
  return Object.assign({}, a, {
    phones: phones,
    callbackPhones: uniq((a.callbackPhones || []).concat(b.callbackPhones || [])).filter(function (x) { return phones.indexOf(x) < 0; }),
    emails: uniq((a.emails || []).concat(b.emails || [])),
    refs: uniq((a.refs || []).concat(b.refs || [])),
    loanNos: uniq((a.loanNos || []).concat(b.loanNos || [])),
    names: uniq((a.names || []).concat(b.names || [])),
    firstAt: firstAt, lastAt: maxDay([a.lastAt, b.lastAt, firstAt]),
    firstCallAt: firstCallAt, firstEmailAt: firstEmailAt,
    method: methodOf(hasCall(a.method) || hasCall(b.method), hasEmail(a.method) || hasEmail(b.method)),
    firstMethod: firstEmailAt && (!firstCallAt || firstEmailAt < firstCallAt) ? "Email" : firstCallAt ? "Call" : (a.firstMethod || b.firstMethod || "Call"),
    kind: strongerKind(a.kind, b.kind),
    reasons: uniq((a.reasons || []).concat(b.reasons || [])),
    said: said || "",
    sources: uniq((a.sources || []).concat(b.sources || [])),
    rows: Math.max(a.rows || 0, b.rows || 0),
    facts: {
      ids: uniq((fa.ids || []).concat(fb.ids || [])).slice(0, 8),
      vmNames: uniq((fa.vmNames || []).concat(fb.vmNames || [])),
      vm: Math.max(fa.vm || 0, fb.vm || 0), answered: Math.max(fa.answered || 0, fb.answered || 0), missed: Math.max(fa.missed || 0, fb.missed || 0),
      statuses: uniq((fa.statuses || []).concat(fb.statuses || [])),
      // the flag belongs to whoever holds the earliest evidence
      returned: b.firstAt && (!a.firstAt || b.firstAt < a.firstAt) ? !!fb.returned : !!fa.returned,
      apprDays: uniq((fa.apprDays || []).concat(fb.apprDays || [])),
      places: { states: uniq((pa.states || []).concat(pb.states || [])), cities: uniq((pa.cities || []).concat(pb.cities || [])), streets: uniq((pa.streets || []).concat(pb.streets || [])) },
      // only-on-a-vendor's-line holds only while both sides came in that way
      viaVendor: (fa.viaVendor || []).length && (fb.viaVendor || []).length ? uniq(fa.viaVendor.concat(fb.viaVendor)) : [],
    },
    decisions: decisions,
  });
}
const SIG_FIELDS = ["phones", "callbackPhones", "emails", "refs", "loanNos", "names", "firstAt", "lastAt", "firstCallAt", "firstEmailAt", "method", "firstMethod", "kind", "reasons", "said", "sources", "rows", "facts", "decisions"];
function sigOf(p) { return JSON.stringify(SIG_FIELDS.map(function (k) { return p[k] == null ? null : p[k]; })); }
// A new import into the stored people, BY KEY (any shared phone or email).
//   new key set          → a new person (fresh id)
//   overlaps one         → that person, its evidence widened: a newer export
//                          moves lastAt later and firstAt earlier; kind only
//                          ever strengthens (response → removal → file)
//   overlaps several     → they were one person all along: folded into the
//                          oldest, the others marked mergedInto (rows are
//                          never deleted)
// His decisions always stay. → {people, changed:[ids], added, updated}
export function mergeImport(stored, incoming, opts) {
  const o = opts || {};
  const now = o.now || new Date().toISOString();
  // Stored people who already share a key (two devices that imported before
  // the SQL was run, two stale tabs) are folded first — the backstop for
  // review G; otherwise the index below would keep only one of them.
  const pre = foldDuplicates(stored || []);
  const list = pre.people;
  const pos = new Map(); list.forEach(function (p, i) { pos.set(p.id, i); });
  const owner = new Map();
  const target = function (id) { let p = list[pos.get(id)]; let n = 0; while (p && p.decisions && p.decisions.mergedInto && pos.has(p.decisions.mergedInto) && n++ < 20) p = list[pos.get(p.decisions.mergedInto)]; return p; };
  const keysOf = personKeys;
  const index = function (p) { keysOf(p).forEach(function (k) { owner.set(k, p.id); }); };
  list.forEach(function (p) { if (isActive(p)) index(p); });
  list.forEach(function (p) { if (!isActive(p)) keysOf(p).forEach(function (k) { if (!owner.has(k)) owner.set(k, p.id); }); });
  const changed = new Set(pre.changed);
  let added = 0, updated = pre.targets.length;
  const taken = function (id) { return pos.has(id); };
  (incoming || []).forEach(function (inc) {
    const hits = [];
    keysOf(inc).forEach(function (k) { const id = owner.get(k); if (id) hits.push(id); });
    const ids = uniq(hits.map(function (id) { const t = target(id); return t ? t.id : null; }).filter(Boolean));
    if (!ids.length) {
      const id = o.newId ? o.newId(taken) : newPersonId(taken);
      const p = Object.assign({}, inc, { id: id, decisions: {}, segment: inc.segment || "unknown", leadId: null, loanId: null, match: "none", likelyLeadIds: [], createdAt: now, updatedAt: now });
      pos.set(id, list.length); list.push(p); index(p); changed.add(id); added++;
      return;
    }
    ids.sort(function (a, b) { const A = list[pos.get(a)], B = list[pos.get(b)]; return msOf(A.createdAt) - msOf(B.createdAt) || (a < b ? -1 : 1); });
    const tid = ids[0];
    const before = sigOf(list[pos.get(tid)]);
    ids.slice(1).forEach(function (oid) {
      mergeWithin(list, pos, pos.get(tid), pos.get(oid), false).forEach(function (id) { if (id !== tid) changed.add(id); });
    });
    // on the person's own evidence, or on the child he merged in whose keys it
    // carries (review S4 — an undo takes it with them)
    let t = routeImport(list, pos, list[pos.get(tid)], inc, changed);
    t = foldPerson(t, inc);
    if (sigOf(t) !== before) { t = Object.assign({}, t, { updatedAt: stampAfter(t.updatedAt) }); changed.add(tid); updated++; }
    list[pos.get(tid)] = t;
    index(t);
  });
  return { people: list, changed: Array.from(changed), added: added, updated: updated };
}
// The preview's "X new, Y updated", against the SAVED log (review S5 — a
// second export dropped onto an open preview never adds its counts on top):
// new = active people not saved yet; updated = saved people this import
// changes (still active).
export function importCounts(saved, people, changed) {
  const S = new Map((saved || []).map(function (p) { return [p.id, p]; }));
  const ch = new Set(changed || []);
  let added = 0, updated = 0;
  (people || []).forEach(function (p) {
    if (!isActive(p)) return;
    if (!S.has(p.id)) added++;
    else if (ch.has(p.id) && S.get(p.id) !== p) updated++;
  });
  return { added: added, updated: updated };
}
// Keys: phones, emails, refs — never callbackPhones (a number only said in a
// voicemail may be someone else's: an office, a counselor, a relative).
function personKeys(p) { return (p.phones || []).map(function (x) { return "p" + x; }).concat((p.emails || []).map(function (x) { return "e" + x; }), (p.refs || []).map(function (x) { return "r" + x; })); }
// A decision: patch sets keys, drop removes them. Every key he touches is
// stamped (decisions.stamps[key] = when), so two copies of one person —
// another tab, another device, this device's copy — merge key by key, the
// newest answer winning (mergeDecisions; review EF).
export function withDecision(p, patch, drop) {
  const t = stampAfter(p.updatedAt);
  const d = Object.assign({}, p.decisions || {}, patch || {});
  const st = Object.assign({}, (p.decisions && p.decisions.stamps) || {});
  (drop || []).forEach(function (k) { delete d[k]; });
  Object.keys(patch || {}).concat(drop || []).forEach(function (k) { if (k !== "stamps") st[k] = t; });
  d.stamps = st;
  return Object.assign({}, p, { decisions: d, updatedAt: t });
}
// One "Check these" answer: "keep" (counts, flag cleared) or "not" (not a
// response — out of every count).
export function decideFlag(p, key, value) {
  const check = Object.assign({}, (p.decisions && p.decisions.check) || {});
  if (value) check[key] = value; else delete check[key];
  const patch = { check: check };
  if (value === "not") patch.notResponse = true;
  else if (value === "keep" && p.decisions && p.decisions.notResponse && !Object.keys(check).some(function (k) { return check[k] === "not"; })) patch.notResponse = false;
  return withDecision(p, patch);
}
export function setNotResponse(p, on) {
  const check = Object.assign({}, (p.decisions && p.decisions.check) || {});
  if (!on) Object.keys(check).forEach(function (k) { if (check[k] === "not") delete check[k]; });
  return withDecision(p, { notResponse: !!on, check: check });
}
// ── "Same person as …" and its undo (review S4) ──
// Evidence = what the log says about a person (keys, dates, kind, rows…);
// decisions are his. A person he merged by hand stays a CHILD of the person
// it went into: decisions.unmerge[child id] = the parent's decision keys
// before it (to take back the decisions only the child brought) and its
// stored link before it (.link — review S4, round 4), and the parent keeps
// its OWN evidence apart — decisions.own: its rows plus every later import
// that came in on its own keys. Its evidence is always its own + every
// child's, so an undo takes off what the child brought (keys, rows, dates,
// kind, and the lead link — linkWithout) and keeps what came later, and
// leaves no shared key that would fold them back together on the next load.
// An import on a child's keys goes on the child (routeImport); one that ties
// two of them by key makes that merge permanent. A merged person's own
// children move up to the person it went into (mergeWithin — never a chain),
// so each merge is undone on its own. The automatic folds (a shared key) are
// permanent: into own.
const EVIDENCE = ["phones", "callbackPhones", "emails", "refs", "loanNos", "names", "firstAt", "lastAt", "firstCallAt", "firstEmailAt", "method", "firstMethod", "kind", "reasons", "said", "sources", "rows", "facts"];
const CARRIED = ["pick", "dnc", "marked", "notResponse"];
function evidenceOf(p) { const e = {}; EVIDENCE.forEach(function (k) { e[k] = p && p[k] !== undefined ? p[k] : null; }); return e; }
function foldEvidence(a, b) { return evidenceOf(foldPerson(Object.assign({}, a, { decisions: {} }), Object.assign({}, b, { decisions: {} }))); }
// The people merged into p by hand that can still be undone, in merge order:
// by each snapshot's seq (review S4, round 5) — never by key order, because
// Postgres jsonb stores object keys sorted (shorter first, then bytewise), so
// once a person comes back from contact_responses its unmerge keys are in id
// order, not merge order. A snapshot from before seq was kept is older than
// any with one; those keep their key order among themselves.
function seqOf(s) { return s && typeof s.seq === "number" ? s.seq : null; }
function orderedChildIds(um) {
  return Object.keys(um || {}).map(function (id, i) { return { id: id, i: i, s: seqOf(um[id]) }; })
    .sort(function (a, b) {
      if (a.s === null && b.s === null) return a.i - b.i;
      if (a.s === null) return -1;
      if (b.s === null) return 1;
      return a.s - b.s || a.i - b.i;
    })
    .map(function (x) { return x.id; });
}
function nextSeq(um) { let m = 0; Object.keys(um || {}).forEach(function (k) { const q = seqOf(um[k]); if (q !== null && q > m) m = q; }); return m + 1; }
function childIds(p) { return orderedChildIds(((p && p.decisions) || {}).unmerge); }
// p's own evidence (without its children's). A record from before `own` was
// kept: its first open merge's snapshot (p as it was before that merge).
function ownOf(p) {
  const d = (p && p.decisions) || {};
  if (d.own) return d.own;
  const first = childIds(p)[0], s = first ? d.unmerge[first] : null;
  return s && s.phones !== undefined ? evidenceOf(s) : evidenceOf(p);
}
// p rebuilt from its own evidence + the children still merged in.
function rebuilt(p, kids) { let e = ownOf(p); (kids || []).forEach(function (c) { e = foldEvidence(e, c); }); return Object.assign({}, p, e); }
// The stored link: match / lead_id / loan_id / segment / likely lead ids.
function storedLink(p) { return { match: (p && p.match) || "none", leadId: (p && p.leadId) || null, loanId: (p && p.loanId) || null, segment: (p && p.segment) || "unknown", likelyLeadIds: ((p && p.likelyLeadIds) || []).slice() }; }
function decSnapOf(p) { const d = p.decisions || {}; return { decKeys: CARRIED.filter(function (k) { return d[k] !== undefined; }), checkKeys: Object.keys(d.check || {}), link: storedLink(p) }; }
// The parent's stored link once child `child` is undone (review S4, round
// 4): what it was linked to before that merge (snap.link). Without it a sure
// lead or likely ids only the child brought stayed — withMatch never
// downgrades a sure link it can't see again and likely ids only grow — so
// the parent stayed joined into the child's record in responsesForCost and
// the undone person never counted again. A pick of his that still stands
// decides instead (withMatch keeps a confirmed one; "None of these"
// re-derives the likely ids exactly). A merge recorded before .link was
// kept: a lead / loan that is the child's is cleared, and the child's likely
// ids. The screen's withMatch then re-derives a real match from the evidence
// left. → the link fields to set ({} = unchanged).
function linkWithout(t, snap, child) {
  if (((t && t.decisions) || {}).pick) return {};
  const cur = storedLink(t);
  let next;
  if (snap && snap.link) next = storedLink(snap.link);
  else {
    const c = storedLink(child);
    next = (cur.leadId && cur.leadId === c.leadId) || (cur.loanId && cur.loanId === c.loanId) ? unlinked() : Object.assign({}, cur);
    const theirs = new Set(c.likelyLeadIds.concat(c.leadId ? [c.leadId] : []));
    next.likelyLeadIds = next.likelyLeadIds.filter(function (id) { return !theirs.has(id); });
    if (next.match === "likely" && !next.likelyLeadIds.length) next = unlinked();
  }
  // no pick stands, so nothing is "confirmed" (withMatch would clear it too)
  if (next.match === "confirmed") next = unlinked();
  const same = cur.match === next.match && cur.leadId === next.leadId && cur.loanId === next.loanId && cur.likelyLeadIds.join("|") === next.likelyLeadIds.join("|");
  return same ? {} : next;
}
// The parent's decisions without child `cid`: what only the child brought (a
// pick, Not a response, a Keep the parent didn't have before it) comes off;
// his later decisions stay. → {patch, drop} for withDecision.
function decisionsWithout(pd, cid, cd) {
  const s = (pd.unmerge || {})[cid] || {}, c = cd || {};
  const same = function (x, y) { return JSON.stringify(x) === JSON.stringify(y); };
  const drop = CARRIED.filter(function (k) { return (s.decKeys || []).indexOf(k) < 0 && pd[k] !== undefined && same(pd[k], c[k]); });
  const patch = { absorbed: (pd.absorbed || []).filter(function (id) { return id !== cid; }) };
  const um = Object.assign({}, pd.unmerge || {}); delete um[cid];
  // a child merged after this one took its link snapshot with this one's
  // link in it: it gets this one's, so its own undo later never puts this
  // one's lead back (review S4, round 4)
  if (s.link) { const ids = orderedChildIds(pd.unmerge); ids.slice(ids.indexOf(cid) + 1).forEach(function (k) { if (um[k] && um[k].link) um[k] = Object.assign({}, um[k], { link: s.link }); }); }
  if (Object.keys(um).length) patch.unmerge = um;
  else { drop.push("unmerge"); if (pd.own !== undefined) drop.push("own"); }
  if (pd.check) {
    const check = Object.assign({}, pd.check);
    Object.keys(check).forEach(function (f) { if ((s.checkKeys || []).indexOf(f) < 0 && (c.check || {})[f] === check[f]) delete check[f]; });
    patch.check = check;
  }
  return { patch: patch, drop: drop };
}
// Fold `other` into `target`; other is kept, marked. opts.manual = his click
// (an undoable child, above); otherwise an automatic fold (a shared key).
export function mergeInto(target, other, opts) {
  const manual = !!(opts && opts.manual);
  const td = target.decisions || {};
  const hasKids = childIds(target).length > 0;
  let t = foldPerson(target, other);
  const patch = { absorbed: uniq((td.absorbed || []).concat([other.id])) };
  if (manual) {
    const um = Object.assign({}, td.unmerge || {}); um[other.id] = Object.assign(decSnapOf(target), { seq: nextSeq(td.unmerge) });
    patch.unmerge = um;
    patch.own = hasKids ? ownOf(target) : evidenceOf(target);
  } else if (hasKids) patch.own = foldEvidence(ownOf(target), other);
  t = withDecision(t, patch);
  const o2 = withDecision(other, manual ? { mergedInto: target.id, mergedBy: "you" } : { mergedInto: target.id });
  return { target: t, other: o2 };
}
function kidsIn(list, pos, p) { return childIds(p).map(function (id) { return pos.has(id) ? list[pos.get(id)] : null; }).filter(function (c) { return c && (c.decisions || {}).mergedInto === p.id; }); }
// A child that shares a number / email / ref with the rest of the person (its
// own evidence, another child) can't be split off again — an undo would fold
// right back on the next load — so that merge becomes permanent (into own):
// the automatic rule's verdict. → the person (the same object when none).
function settleTies(list, pos, t) {
  const kids = kidsIn(list, pos, t);
  if (!kids.length) return t;
  let own = ownOf(t);
  const um = Object.assign({}, (t.decisions || {}).unmerge || {});
  let tied = false, again = true;
  while (again) {
    again = false;
    const live = kids.filter(function (c) { return um[c.id]; });
    for (let i = 0; i < live.length && !again; i++) {
      const ck = new Set(personKeys(live[i]));
      const rest = [own].concat(live.filter(function (x) { return x !== live[i]; }));
      if (rest.some(function (e) { return personKeys(e).some(function (k) { return ck.has(k); }); })) { own = foldEvidence(own, live[i]); delete um[live[i].id]; tied = again = true; }
    }
  }
  if (!tied) return t;
  return Object.keys(um).length ? withDecision(t, { unmerge: um, own: own }) : withDecision(t, {}, ["unmerge", "own"]);
}
// mergeInto inside a list of people (indexes ti / oi; pos = id → index): a
// person with children of its own goes in alone and its children move up to
// the target as the target's own children — never a chain. → the ids written.
function mergeWithin(list, pos, ti, oi, manual) {
  const get = function (id) { return pos.has(id) ? list[pos.get(id)] : null; };
  const t0 = list[ti], o0 = list[oi];
  const kids = childIds(o0).map(get).filter(function (c) { return c && (c.decisions || {}).mergedInto === o0.id; });
  let o = o0;
  kids.forEach(function (c) { const w = decisionsWithout(o.decisions || {}, c.id, c.decisions); o = withDecision(o, w.patch, w.drop); });
  if (kids.length) o = Object.assign({}, o, ownOf(o0));
  const r = mergeInto(t0, o, { manual: manual });
  let t = r.target;
  list[oi] = r.other;
  const out = [t0.id, o0.id];
  if (kids.length) {
    const td = t.decisions || {};
    const own = td.own || evidenceOf(t), um = Object.assign({}, td.unmerge || {}), snap = decSnapOf(t0);
    kids.forEach(function (c) {
      t = foldPerson(t, c);
      um[c.id] = Object.assign({}, snap, { seq: nextSeq(um) });
      list[pos.get(c.id)] = withDecision(c, { mergedInto: t0.id });
      out.push(c.id);
    });
    t = withDecision(t, { own: own, unmerge: um, absorbed: uniq(((t.decisions || {}).absorbed || []).concat(kids.map(function (c) { return c.id; }))) });
  }
  list[ti] = settleTies(list, pos, t);
  return out;
}
function listPos(list) { const pos = new Map(); list.forEach(function (p, i) { if (p && p.id && !pos.has(p.id)) pos.set(p.id, i); }); return pos; }
// His "Same person as …" (the button, or the list + Merge): `other` is folded
// into `target`. Only between two people who both count — never into someone
// merged away or marked Not a response (a choice left over from before he
// decided them — review S4). → {people, changed, target, other}, or null.
export function mergeSamePerson(people, targetId, otherId) {
  const list = (people || []).slice();
  const pos = listPos(list);
  const t = pos.has(targetId) ? list[pos.get(targetId)] : null, o = pos.has(otherId) ? list[pos.get(otherId)] : null;
  if (!t || !o || t.id === o.id || !counts(t) || !counts(o)) return null;
  const ids = uniq(mergeWithin(list, pos, pos.get(targetId), pos.get(otherId), true));
  return { people: list, changed: ids.map(function (id) { return list[pos.get(id)]; }), target: list[pos.get(targetId)], other: list[pos.get(otherId)] };
}
// Undo his "Same person as …" (Merged (N) → undo): the person is on their own
// again with exactly their evidence; the person they went into is rebuilt
// from its own evidence + the children still merged in (and, for a record
// from before children moved up, every person above it), so what a later
// import added stays, what the merge brought goes, and nothing is left
// sharing a key that would fold them back together on the next load. Its
// stored link goes back to what it was before the merge (linkWithout).
// → {people, changed, target, other}, or null when it can't be undone (an
// automatic fold, a merge an export has since tied by key, or a child of it
// that isn't in the list).
export function undoMerge(people, otherId) {
  const list = (people || []).slice();
  const pos = listPos(list);
  const get = function (id) { return pos.has(id) ? list[pos.get(id)] : null; };
  const kidsOf = function (p) { return childIds(p).map(get).filter(function (c) { return c && (c.decisions || {}).mergedInto === p.id; }); };
  const other = get(otherId);
  const od = (other && other.decisions) || {};
  const parent = od.mergedInto ? get(od.mergedInto) : null;
  if (!parent || childIds(parent).indexOf(otherId) < 0) return null;
  const rest = childIds(parent).filter(function (id) { return id !== otherId; });
  if (rest.some(function (id) { return !get(id); })) return null;
  const w = decisionsWithout(parent.decisions || {}, otherId, od);
  const t0 = withDecision(rebuilt(parent, kidsOf(parent).filter(function (c) { return c.id !== otherId; })), w.patch, w.drop);
  const t = Object.assign(t0, linkWithout(t0, ((parent.decisions || {}).unmerge || {})[otherId], other));
  const o2 = withDecision(other, {}, ["mergedInto", "mergedBy"]);
  list[pos.get(otherId)] = o2; list[pos.get(parent.id)] = t;
  const changed = [t, o2];
  let cur = t;
  for (let n = 0; n < 20; n++) {
    const up = (cur.decisions || {}).mergedInto ? get(cur.decisions.mergedInto) : null;
    if (!up || childIds(up).indexOf(cur.id) < 0) break;
    const u = rebuilt(up, kidsOf(up));
    if (sigOf(u) === sigOf(up)) break;
    cur = Object.assign(u, { updatedAt: stampAfter(up.updatedAt) });
    list[pos.get(up.id)] = cur; changed.push(cur);
  }
  return { people: list, changed: changed, target: t, other: o2 };
}
// The same, for one target / other (people = the rest of the list, so the
// target's other children are kept). → {target, other, changed} or null.
export function unmergePerson(target, other, people) {
  if (!target || !other || ((other.decisions || {}).mergedInto !== target.id)) return null;
  const list = (people || []).filter(function (p) { return p && p.id !== target.id && p.id !== other.id; }).concat([target, other]);
  const r = undoMerge(list, other.id);
  return r ? { target: r.target, other: r.other, changed: r.changed } : null;
}
// An import that reaches a person with children he merged in goes where its
// keys are: on the child whose number / email it carries (so an undo takes it
// with them), else on the person's own evidence. One that carries the keys of
// two of them ties them by key — those merges become permanent (settleTies),
// what mergeImport does with two stored people an import bridges.
// → the person (a child is written into the list; its id into `changed`).
function routeImport(list, pos, t, inc, changed) {
  const kids = kidsIn(list, pos, t);
  if (!kids.length) return t;
  const ik = new Set(personKeys(inc));
  const hit = function (e) { return personKeys(e).some(function (k) { return ik.has(k); }); };
  const own = ownOf(t);
  const hitKids = kids.filter(hit);
  if (hitKids.length === 1 && !hit(own)) {
    const c = hitKids[0], c2 = Object.assign({}, c, foldEvidence(c, inc));
    if (sigOf(c2) !== sigOf(c)) { list[pos.get(c.id)] = Object.assign(c2, { updatedAt: stampAfter(c.updatedAt) }); changed.add(c.id); }
    return t;
  }
  const own2 = foldEvidence(own, inc);
  return settleTies(list, pos, sameJson(own2, (t.decisions || {}).own) ? t : withDecision(t, { own: own2 }));
}
// His pick for a likely (or wrong sure) match: {leadId, loanId, segment?} or
// null = "not them" (none). The stored link follows his answer at once: a
// pick links that record (segment = the picked lead's, when the screen knows
// it); "None of these" clears lead_id / loan_id / segment, so nothing
// downstream (responsesForCost) keeps a lead he said is not them.
function unlinked() { return { match: "none", leadId: null, loanId: null, segment: "unknown", likelyLeadIds: [] }; }
export function pickMatch(p, pick) {
  const n = withDecision(p, { pick: pick ? { leadId: pick.leadId || null, loanId: pick.loanId || null } : { none: true } });
  if (!pick) return Object.assign(n, unlinked());
  const leadId = pick.leadId || null, loanId = pick.loanId || null;
  const same = leadId === (p.leadId || null) && loanId === (p.loanId || null);
  return Object.assign(n, { match: "confirmed", leadId: leadId, loanId: loanId, segment: pick.segment || (same && p.segment) || "unknown", likelyLeadIds: [] });
}
// "undo" on his pick: a link that only his pick made is cleared (a sure match
// in memory is written back by withMatch).
export function clearPick(p) {
  const n = withDecision(p, {}, ["pick"]);
  return p.match === "confirmed" ? Object.assign(n, unlinked()) : n;
}
// The stored link (match / lead_id / loan_id / segment / likely lead ids)
// from the live match m — the same object when nothing changes:
//   sure / confirmed  written; a known segment is never replaced by
//                     "unknown" / "other" only because the lead isn't in
//                     memory this session
//   his decision      "None of these", or his pick undone → the link is
//                     cleared (likely again when there are candidates)
//   likely            "likely" + the candidate lead ids (never linked; kept
//                     for cost per response to spot a lead the CRM already
//                     counts — they only ever grow, until he decides)
//   anything else     unchanged: a sure match that merely isn't in memory
//                     this session is never downgraded
function candidateLeadIds(m) { return uniq(((m && m.candidates) || []).map(function (c) { return c.kind === "lead" ? c.id : c.leadId; }).filter(Boolean)); }
// A live "likely" that is the stored sure record competing with another one
// for the same phone / email — not a name + place guess (that only means the
// stored lead isn't loaded, and never downgrades a sure match).
function ambiguousWith(p, m) {
  const cs = (m && m.candidates) || [];
  if (!cs.some(function (c) { return c.why === "phone / email"; })) return false;
  return cs.some(function (c) { return (p.leadId && (c.kind === "lead" ? c.id : c.leadId) === p.leadId) || (p.loanId && (c.kind === "loan" ? c.id : c.loanId) === p.loanId); });
}
export function withMatch(p, m) {
  if (!p || !m) return p;
  const dec = p.decisions || {};
  const linked = p.match === "sure" || p.match === "confirmed";
  let next;
  if (m.match === "sure" || m.match === "confirmed") {
    const leadId = m.leadId || null, loanId = m.loanId || null;
    let segment = m.segment || "unknown";
    if (!m.lead && (leadId || loanId) && leadId === (p.leadId || null) && loanId === (p.loanId || null) && p.segment && p.segment !== "unknown") segment = p.segment;
    next = { match: m.match, leadId: leadId, loanId: loanId, segment: segment, likelyLeadIds: [] };
  } else if ((dec.pick && dec.pick.none) || (!dec.pick && p.match === "confirmed")) {
    next = m.match === "likely" ? { match: "likely", leadId: null, loanId: null, segment: "unknown", likelyLeadIds: candidateLeadIds(m) } : unlinked();
  } else if (m.match === "likely" && p.match === "sure" && ambiguousWith(p, m)) {
    // the stored sure lead now shares the number / email with another record
    // (the database search found it): ambiguous — his call, never a link (review B)
    next = { match: "likely", leadId: null, loanId: null, segment: "unknown", likelyLeadIds: candidateLeadIds(m) };
  } else if (m.match === "likely" && !linked) {
    next = { match: "likely", leadId: null, loanId: null, segment: "unknown", likelyLeadIds: uniq((p.likelyLeadIds || []).concat(candidateLeadIds(m))) };
  } else return p;
  const same = Object.keys(next).every(function (k) { return k === "likelyLeadIds" ? (p.likelyLeadIds || []).join("|") === next.likelyLeadIds.join("|") : (p[k] == null ? null : p[k]) === next[k]; });
  return same ? p : Object.assign({}, p, next, { updatedAt: stampAfter(p.updatedAt) });
}

// ── two copies of one person (review EF / G) ──
function canon(v) {
  if (Array.isArray(v)) return v.map(canon);
  if (v && typeof v === "object") { const o = {}; Object.keys(v).sort().forEach(function (k) { o[k] = canon(v[k]); }); return o; }
  return v;
}
const sameJson = function (a, b) { return JSON.stringify(canon(a == null ? null : a)) === JSON.stringify(canon(b == null ? null : b)); };
// Two versions of one person's decisions → one, key by key: the key's newer
// stamp wins (a removal counts — a key he dropped later stays dropped);
// unstamped / equal → `bWinsTies` (the newer copy of the person).
export function mergeDecisions(da, db, bWinsTies) {
  const a = da || {}, b = db || {};
  const sa = a.stamps || {}, sb = b.stamps || {};
  const out = {}, st = {};
  uniq(Object.keys(a).concat(Object.keys(b), Object.keys(sa), Object.keys(sb))).forEach(function (k) {
    if (k === "stamps") return;
    const ta = msOf(sa[k]), tb = msOf(sb[k]);
    const useB = tb > ta || (tb === ta && !!bWinsTies);
    const src = useB ? b : a, ss = useB ? sb : sa;
    if (Object.prototype.hasOwnProperty.call(src, k)) out[k] = src[k];
    if (ss[k]) st[k] = ss[k];
  });
  if (Object.keys(st).length) out.stamps = st;
  return out;
}
// This copy (`mine`) and another (`theirs`: another tab's write to this
// device's copy, the table's row) → one. The evidence and the stored link are
// the newer copy's; the decisions merge key by key (a pick that came from the
// other copy brings its link). Returns `mine` / `theirs` themselves when one
// already says it all.
export function mergePersonVersions(mine, theirs) {
  if (!theirs || theirs === mine) return mine;
  if (!mine) return theirs;
  const theirsNewer = msOf(theirs.updatedAt) > msOf(mine.updatedAt);
  const base = theirsNewer ? theirs : mine, other = theirsNewer ? mine : theirs;
  const d = mergeDecisions(mine.decisions, theirs.decisions, theirsNewer);
  if (sameJson(d, base.decisions || {})) return base;
  const link = sameJson(d.pick, (base.decisions || {}).pick) ? {} : { match: other.match, leadId: other.leadId || null, loanId: other.loanId || null, segment: other.segment || "unknown", likelyLeadIds: other.likelyLeadIds || [] };
  return Object.assign({}, base, link, { decisions: d, updatedAt: stampAfter(base.updatedAt) });
}
// The screen takes in another copy of the people (another tab's write, a
// refresh from the table): person by person (mergePersonVersions); people it
// didn't have are added. The SAME array when nothing changes.
export function absorbPeople(cur, incoming) {
  const list = (cur || []).slice();
  const pos = new Map(); list.forEach(function (p, i) { pos.set(p.id, i); });
  let changed = false;
  (incoming || []).forEach(function (q) {
    if (!q || !q.id) return;
    if (!pos.has(q.id)) { pos.set(q.id, list.length); list.push(q); changed = true; return; }
    const i = pos.get(q.id), m = mergePersonVersions(list[i], q);
    if (m !== list[i]) { list[i] = m; changed = true; }
  });
  return changed ? list : (cur || []);
}
// Active people who share a key (phone / email / ref) are ONE person — fold
// them (two devices that imported before the SQL was run, two stale tabs):
// into the preferred one (opts.prefer(id): a row the table already has), then
// the oldest; the others are kept, marked mergedInto; decisions kept.
// → {people, changed:[ids], targets:[ids]}
export function foldDuplicates(people, opts) {
  const o = opts || {};
  const prefer = o.prefer || function () { return false; };
  const list = (people || []).slice();
  const par = new Map();
  const find = function (i) { while (par.get(i) !== i) { par.set(i, par.get(par.get(i))); i = par.get(i); } return i; };
  const owner = new Map();
  list.forEach(function (p, i) {
    if (!isActive(p)) return;
    par.set(i, i);
    personKeys(p).forEach(function (k) {
      if (!owner.has(k)) { owner.set(k, i); return; }
      const x = find(owner.get(k)), y = find(i);
      if (x !== y) par.set(y, x);
    });
  });
  const groups = new Map();
  par.forEach(function (_v, i) { const r = find(i); if (!groups.has(r)) groups.set(r, []); groups.get(r).push(i); });
  const changed = [], targets = [];
  const pos = listPos(list);
  groups.forEach(function (ix) {
    if (ix.length < 2) return;
    ix.sort(function (a, b) { return (prefer(list[b].id) ? 1 : 0) - (prefer(list[a].id) ? 1 : 0) || msOf(list[a].createdAt) - msOf(list[b].createdAt) || (list[a].id < list[b].id ? -1 : 1); });
    const tid = list[ix[0]].id;
    // a person's children he merged in move up with it (mergeWithin)
    ix.slice(1).forEach(function (oi) { mergeWithin(list, pos, ix[0], oi, false).forEach(function (id) { if (id !== tid) changed.push(id); }); });
    changed.push(tid); targets.push(tid);
  });
  return { people: list, changed: changed, targets: targets };
}

// ═══════ 6. matching people to the CRM ═══════
function leadTokens(name) { return toks(name).filter(function (t) { return !NAME_NOISE.has(t); }); }
function streetKey(addr) {
  const m = /^\s*(\d{1,6})\s+(?:(?:N|S|E|W|NE|NW|SE|SW|North|South|East|West)\.?\s+)?([A-Za-z][A-Za-z']{2,})/i.exec(String(addr || ""));
  return m ? m[1] + " " + m[2].toLowerCase() : "";
}
// leads / loans (short keys: b, ph, em, prop, sourceLeadId) / outbound rows
// ({lead_id, phone_digits | phones:[{n}]}) → the lookup tables.
export function buildCrmIndex(crm) {
  const c = crm || {};
  const leads = (c.leads || []).filter(function (l) { return l && l.id; });
  const loans = (c.loans || []).filter(function (l) { return l && l.id; });
  const ix = { leadById: new Map(), loanById: new Map(), leadByPhone: new Map(), leadByEmail: new Map(), loanByPhone: new Map(), loanByEmail: new Map(), outByPhone: new Map(), leadByToken: new Map(), loanByToken: new Map(), leadOfLoan: new Map(), leadTok: new Map(), loanTok: new Map() };
  const push = function (m, k, v) { if (!k) return; const a = m.get(k); if (a) { if (a.indexOf(v) < 0) a.push(v); } else m.set(k, [v]); };
  const emailsOf = function (s) { return String(s || "").toLowerCase().split(/[;,\s]+/).filter(function (e) { return /@/.test(e); }); };
  leads.forEach(function (l) {
    if (ix.leadById.has(l.id)) return;
    ix.leadById.set(l.id, l);
    const p = d10(l.phone); if (p.length === 10) push(ix.leadByPhone, p, l);
    emailsOf(l.email).forEach(function (e) { push(ix.leadByEmail, e, l); });
    const t = leadTokens(l.name); ix.leadTok.set(l.id, t);
    uniq(t).forEach(function (w) { if (w.length >= 3) push(ix.leadByToken, w, l); });
    if (l.converted_loan_id) ix.leadOfLoan.set(l.converted_loan_id, l.id);
  });
  loans.forEach(function (l) {
    if (ix.loanById.has(l.id)) return;
    ix.loanById.set(l.id, l);
    const p = d10(l.ph != null ? l.ph : l.phone); if (p.length === 10) push(ix.loanByPhone, p, l);
    emailsOf(l.em != null ? l.em : l.email).forEach(function (e) { push(ix.loanByEmail, e, l); });
    const t = leadTokens(l.b != null ? l.b : l.borrower_name); ix.loanTok.set(l.id, t);
    uniq(t).forEach(function (w) { if (w.length >= 3) push(ix.loanByToken, w, l); });
    if (l.sourceLeadId && !ix.leadOfLoan.has(l.id)) ix.leadOfLoan.set(l.id, l.sourceLeadId);
  });
  (c.outbound || []).forEach(function (o) {
    if (!o || !o.lead_id) return;
    const nums = (o.phone_digits || []).concat((o.phones || []).map(function (x) { return x && x.n; }));
    uniq(nums.map(d10)).forEach(function (p) { if (p.length === 10) push(ix.outByPhone, p, o.lead_id); });
  });
  return ix;
}
function fileOfLead(ix, lead) {
  if (!lead) return null;
  if (lead.converted_loan_id && ix.loanById.has(lead.converted_loan_id)) return ix.loanById.get(lead.converted_loan_id);
  let hit = null;
  ix.loanById.forEach(function (l) { if (!hit && l.sourceLeadId === lead.id) hit = l; });
  return hit;
}
function nameHits(pair, T) {
  const a = pair[0], b = pair[1];
  if (a === b) return false;
  const near = function (x) { return x.length >= 5 && T.some(function (t) { return t.length >= 5 && t !== a && t !== b && within1(x, t); }); };
  const ha = T.indexOf(a) >= 0, hb = T.indexOf(b) >= 0;
  return (ha && a.length >= 3 && (hb || near(b))) || (hb && b.length >= 3 && near(a));
}
function placeScore(pl, rec) {
  if (!pl) return 0;
  const st = String(rec.state || "").toUpperCase(), city = String(rec.city || "").toLowerCase(), street = streetKey(rec.address);
  if (street && (pl.streets || []).indexOf(street) >= 0) return 3;
  if (city && (pl.cities || []).indexOf(city) >= 0) return 2;
  if (st && (pl.states || []).indexOf(st) >= 0) return 1;
  return 0;
}
const PLACE_WHY = { 3: "same street", 2: "same city", 1: "same state" };
function pairsOf(p) {
  const out = [];
  (p.names || []).forEach(function (n) { n.split(/\s*\/\s*|\s*&\s*/).forEach(function (part) { const t = toks(part.replace(",", " ")); if (t.length >= 2) out.push([t[0], t[1]]); }); });
  return out;
}
// One person → {match, leadId, loanId, via, candidates:[{kind, id, label, why, score}]}
export function matchPerson(p, ix) {
  const dec = (p && p.decisions) || {};
  if (dec.pick) {
    if (dec.pick.none) return { match: "none", leadId: null, loanId: null, via: "you said not them", candidates: [] };
    return { match: "confirmed", leadId: dec.pick.leadId || null, loanId: dec.pick.loanId || null, via: "you picked", candidates: [] };
  }
  const leads = new Map(), loans = new Map(), via = [];
  const addLead = function (l, why) { if (l && !leads.has(l.id)) leads.set(l.id, l); if (why) via.push(why); };
  const addLoan = function (l, why) { if (l && !loans.has(l.id)) loans.set(l.id, l); if (why) via.push(why); };
  uniq((p.phones || []).concat(p.callbackPhones || [])).forEach(function (x) {
    (ix.leadByPhone.get(x) || []).forEach(function (l) { addLead(l, "phone on the lead"); });
    (ix.loanByPhone.get(x) || []).forEach(function (l) { addLoan(l, "phone on the file"); });
    (ix.outByPhone.get(x) || []).forEach(function (id) { const l = ix.leadById.get(id); if (l) addLead(l, "skip-traced number"); });
  });
  (p.emails || []).forEach(function (x) {
    (ix.leadByEmail.get(x) || []).forEach(function (l) { addLead(l, "email on the lead"); });
    (ix.loanByEmail.get(x) || []).forEach(function (l) { addLoan(l, "email on the file"); });
  });
  // A file and the lead it came from are one record.
  loans.forEach(function (l) { const lid = ix.leadOfLoan.get(l.id); if (lid && ix.leadById.has(lid)) addLead(ix.leadById.get(lid)); });
  leads.forEach(function (l) { const f = fileOfLead(ix, l); if (f) addLoan(f); });
  const leadIds = Array.from(leads.keys()), loanIds = Array.from(loans.keys());
  // Records that belong together (a lead + its file) count as one.
  const people = new Set();
  leadIds.forEach(function (id) { const f = fileOfLead(ix, leads.get(id)); people.add(f ? "f" + f.id : "l" + id); });
  loanIds.forEach(function (id) { people.add("f" + id); });
  if (people.size === 1) return { match: "sure", leadId: leadIds[0] || null, loanId: loanIds[0] || null, via: uniq(via).join(" · "), candidates: [] };
  if (people.size > 1) {
    const cands = leadIds.map(function (id) { const l = leads.get(id); return { kind: "lead", id: id, label: recLabel(l), why: "phone / email", score: 4, loanId: (fileOfLead(ix, l) || {}).id || null }; })
      .concat(loanIds.filter(function (id) { return !leadIds.some(function (lid) { const f = fileOfLead(ix, leads.get(lid)); return f && f.id === id; }); })
        .map(function (id) { const l = loans.get(id); return { kind: "loan", id: id, label: recLabel(l), why: "phone / email", score: 4, leadId: ix.leadOfLoan.get(id) || null }; }));
    return { match: "likely", leadId: null, loanId: null, via: "more than one record has this phone / email", candidates: cands.slice(0, 5) };
  }
  // Likely: a name AND a place (street > city > state). Never automatic.
  const pairs = pairsOf(p);
  if (!pairs.length) return { match: "none", leadId: null, loanId: null, via: "", candidates: [] };
  const pl = p.facts && p.facts.places;
  const cands = new Map();
  pairs.forEach(function (pr) {
    const seen = new Set();
    pr.forEach(function (w) {
      (ix.leadByToken.get(w) || []).forEach(function (l) {
        if (seen.has("l" + l.id)) return; seen.add("l" + l.id);
        if (!nameHits(pr, ix.leadTok.get(l.id) || [])) return;
        const s = placeScore(pl, l); if (!s) return;
        const prev = cands.get("l" + l.id);
        if (!prev || prev.score < s) cands.set("l" + l.id, { kind: "lead", id: l.id, label: recLabel(l), why: "name + " + PLACE_WHY[s], score: s, loanId: (fileOfLead(ix, l) || {}).id || null });
      });
      (ix.loanByToken.get(w) || []).forEach(function (l) {
        if (seen.has("f" + l.id)) return; seen.add("f" + l.id);
        if (!nameHits(pr, ix.loanTok.get(l.id) || [])) return;
        const pa = placeFromAddress(l.prop || l.property) || {};
        const s = placeScore(pl, { state: pa.state, city: pa.city, address: l.prop || l.property }); if (!s) return;
        const lid = ix.leadOfLoan.get(l.id) || null;
        if (lid && cands.has("l" + lid)) return;
        const prev = cands.get("f" + l.id);
        if (!prev || prev.score < s) cands.set("f" + l.id, { kind: "loan", id: l.id, label: recLabel(l), why: "name + " + PLACE_WHY[s], score: s, leadId: lid });
      });
    });
  });
  const list = Array.from(cands.values()).sort(function (a, b) { return b.score - a.score || (a.label < b.label ? -1 : 1); }).slice(0, 3);
  if (!list.length) return { match: "none", leadId: null, loanId: null, via: "", candidates: [] };
  return { match: "likely", leadId: null, loanId: null, via: list[0].why, candidates: list };
}
function recLabel(r) {
  if (!r) return "";
  if (r.b != null || r.ph != null) return (r.b || "(no name)") + (r.prop ? " · " + r.prop : "") + " · file " + r.id;
  return (r.name || "(no name)") + " · " + [r.address, r.city, r.state].filter(Boolean).join(", ");
}
// Every person → their match + segment. leads may include rows found by the
// database search that aren't in memory (they are adopted only when acted on).
export function matchPeople(people, crm) {
  const ix = crm && crm.leadById ? crm : buildCrmIndex(crm);
  const out = new Map();
  (people || []).forEach(function (p) {
    if (!isActive(p)) return;
    const m = matchPerson(p, ix);
    let lead = m.leadId ? ix.leadById.get(m.leadId) : null;
    if (!lead && m.loanId) { const lid = ix.leadOfLoan.get(m.loanId); if (lid) lead = ix.leadById.get(lid) || null; }
    // a file's own lead is its lead even while that lead isn't in memory
    m.leadId = m.leadId || (lead ? lead.id : null) || (m.loanId ? ix.leadOfLoan.get(m.loanId) || null : null);
    m.segment = lead ? segmentOf(lead) : m.loanId ? "other" : "unknown";
    m.lead = lead || null;
    m.loan = m.loanId ? ix.loanById.get(m.loanId) || null : null;
    out.set(p.id, m);
  });
  return out;
}
// Who still needs the database: no sure / confirmed match in memory.
export function needsRemote(people, matches) {
  return (people || []).filter(function (p) { const m = matches && matches.get(p.id); return counts(p) && (!m || (m.match !== "sure" && m.match !== "confirmed")); });
}
// His confirmed picks whose lead isn't in memory (a lead the database search
// found in an earlier session) — fetched by id so the pick keeps its lead,
// its segment and its buttons.
export function pickLeadIds(people, haveLead) {
  const have = haveLead || function () { return false; };
  return uniq((people || []).filter(counts).map(function (p) { const k = p.decisions && p.decisions.pick; return k && !k.none ? k.leadId : null; })).filter(function (id) { return !have(id); });
}
// What the database is asked, as KEYS — one ask per key per session (the
// screen keeps them in the session memory below), so a decision never asks
// again for people already looked up:
//   p<phone> / e<email> / n<name pair>  the lead search (skipped once every
//                                       lead is in memory)
//   o<phone>                            skip-traced numbers
//   i<lead id>                          a lead fetched by id (his picks)
// A person's callbackPhones are asked too (matched against, never a key).
export function remoteKeys(people, opts) {
  const o = opts || {};
  const keys = [], seen = new Set();
  const add = function (k) { if (!seen.has(k)) { seen.add(k); keys.push(k); } };
  (people || []).forEach(function (p) {
    const ph = uniq((p.phones || []).concat(p.callbackPhones || [])).filter(function (x) { return x.length === 10; });
    if (!o.skipLeads) {
      ph.forEach(function (x) { add("p" + x); });
      (p.emails || []).forEach(function (x) { add("e" + x); });
      pairsOf(p).forEach(function (pr) { if (pr[0].length >= 2 && pr[1].length >= 2) add("n" + pr.slice().sort().join(" ")); });
    }
    ph.forEach(function (x) { add("o" + x); });
  });
  (o.ids || []).forEach(function (id) { if (id) add("i" + id); });
  return keys;
}
// Ask exactly those keys in small batches (api = lib.js searchLeadsByContacts
// / outboundByPhones / fetchLeadsByIds). Never a table load. → {leads,
// outbound, outboundMissing, error, settled: the keys answered (a failed
// request's keys are not — they can be asked again)}
export async function lookupKeys(keys, api, opts) {
  const o = opts || {};
  const a = api || {};
  const by = { p: [], e: [], n: [], o: [], i: [] };
  uniq(keys).forEach(function (k) { const t = k.charAt(0); if (by[t]) by[t].push(k.slice(1)); });
  const out = { leads: [], outbound: [], outboundMissing: false, error: null, settled: [] };
  const settle = function (t, list) { list.forEach(function (v) { out.settled.push(t + v); }); };
  const keep = new Map();
  const add = function (rows) { (rows || []).forEach(function (l) { if (l && l.id && !keep.has(l.id)) keep.set(l.id, l); }); };
  if (a.searchLeadsByContacts && (by.p.length || by.e.length || by.n.length)) {
    const r = await a.searchLeadsByContacts({ phones: by.p, emails: by.e, names: by.n.map(function (s) { return s.split(" "); }) }, { onProgress: o.onProgress });
    if (r && r.error) out.error = r.error; else { settle("p", by.p); settle("e", by.e); settle("n", by.n); }
    add(r && r.data);
  }
  const idList = by.i.slice();
  if (a.outboundByPhones && by.o.length) {
    const r = await a.outboundByPhones(by.o);
    if (r && r.missing) { out.outboundMissing = true; settle("o", by.o); }
    else if (r && r.error) { if (!out.error) out.error = r.error; }
    else settle("o", by.o);
    out.outbound = (r && r.data) || [];
    out.outbound.forEach(function (x) { if (x && x.lead_id) idList.push(x.lead_id); });
  }
  const have = o.haveLead || function () { return false; };
  const ids = uniq(idList).filter(function (id) { return id && !keep.has(id) && !have(id); });
  let idsOk = true;
  if (ids.length && a.fetchLeadsByIds) { const f = await a.fetchLeadsByIds(ids); if (f && f.error) { idsOk = false; if (!out.error) out.error = f.error; } add(f && f.data); }
  if (idsOk) settle("i", by.i);
  out.leads = Array.from(keep.values());
  return out;
}
// The people → their keys not asked yet (opts.asked) → lookupKeys; the keys
// answered join opts.asked. → {leads, outbound, outboundMissing, error, settled}
export async function lookupRemote(people, api, opts) {
  const o = opts || {};
  const asked = o.asked || null;
  const keys = remoteKeys(people, { skipLeads: o.skipLeads, ids: o.ids }).filter(function (k) { return !asked || !asked.has(k); });
  const r = await lookupKeys(keys, api, o);
  if (asked) r.settled.forEach(function (k) { asked.add(k); });
  return r;
}
// The session's lookup memory (the screen's): every key asked once, the leads
// and skip-trace rows it found kept for the session — leaving the tab, a pick,
// "Not a response", "Same person as" or an undo never asks again. Cleared at
// sign-out (resetContactLogSession). `busy` = keys being asked right now.
let lookupMem = null;
lookupMem = freshLookups();
function freshLookups() { return { era: (lookupMem ? lookupMem.era : 0) + 1, asked: new Set(), busy: new Set(), leads: new Map(), outbound: new Map(), outboundMissing: false }; }
export function lookupMemory() { return lookupMem; }
// A screen listens for changes (a lookup started before the tab was left and
// re-opened still refreshes the screen that is open when it lands).
const lookupListeners = new Set();
export function onLookupChange(fn) { lookupListeners.add(fn); return function () { lookupListeners.delete(fn); }; }
function lookupChanged() { lookupListeners.forEach(function (fn) { try { fn(); } catch (e) { /* a screen went away */ } }); }
export function resetContactLookups() { lookupMem = freshLookups(); lookupChanged(); }
// A lookup of `keys` is starting → its memory (to hand back to endLookup).
export function beginLookup(keys) { const mem = lookupMem; (keys || []).forEach(function (k) { mem.busy.add(k); }); lookupChanged(); return mem; }
// Its answer (res from lookupKeys, or null when it threw) into that memory —
// a lookup that outlived a sign-out lands in the old memory, never the new.
export function endLookup(mem, keys, res) {
  (keys || []).forEach(function (k) { mem.busy.delete(k); });
  if (res) {
    (res.settled || []).forEach(function (k) { mem.asked.add(k); });
    (res.leads || []).forEach(function (l) { if (l && l.id) mem.leads.set(l.id, l); });
    (res.outbound || []).forEach(function (x) { if (x) mem.outbound.set(x.id || (x.lead_id + ":" + (x.phone_digits || []).join(",")), x); });
    if (res.outboundMissing) mem.outboundMissing = true;
  }
  lookupChanged();
}
// Sign-out: the table probe ("Saved on this device only" latch) and the
// lookup memory start over for the next login — like resetWhiteboard.
export function resetContactLogSession() { resetContactTable(); resetContactLookups(); }

// ═══════ 7. what the buttons write ═══════
// "Mark N leads as responded": responded + the person's first date + Call /
// Email — through the normal response record (applyResponse). An EARLIER
// responded_at is never moved later (then its method stays too; a missing
// method is filled). Returns the SAME object when nothing would change.
export function leadResponsePatch(lead, person) {
  if (!lead || !person) return lead;
  const day = person.firstAt || null;
  const method = person.firstMethod === "Email" ? "Email" : "Call";
  // the CRM's own response day: responded_at, else the credited mailing's
  // (responseDay — a day kept only on the mailing is never moved later; review C)
  const had = responseDay(lead) || "";
  const patch = {};
  if (day && (!had || day < had)) { patch.date = day; patch.method = method; }
  else if (!normalizeMethod(lead.response_method)) patch.method = method;
  if (!Object.keys(patch).length && lead.responded) return lead;
  const n = applyResponse(lead, patch);
  // applyResponse keeps the credited project mailing's date in step — but a
  // mailing is never marked responded before its own drop.
  if (patch.date && Array.isArray(n.mailings) && Array.isArray(lead.mailings) && n.mailings !== lead.mailings) {
    n.mailings = n.mailings.map(function (m, i) {
      const was = lead.mailings[i];
      const sent = m && m.dateSent ? localDayOf(m.dateSent) : "";
      return m && was && m !== was && sent && m.respondedDate && m.respondedDate < sent ? Object.assign({}, m, { respondedDate: was.respondedDate || "" }) : m;
    });
  }
  return n;
}
// A note on the file's own contact log (the existing path — loans carry
// contactLog in follow_ups._wf). Once per person; same object when present.
export function fileNoteFor(loan, person) {
  if (!loan || !person) return loan;
  const log = Array.isArray(loan.contactLog) ? loan.contactLog : [];
  if (log.some(function (e) { return e && e.contactLogPerson === person.id; })) return loan;
  const e = contactEntry("note", "From the call & email log: first reached out " + (person.firstAt ? fmtDay(person.firstAt) : "(no date)") + " by " + (person.firstMethod || "Call") + (person.method && person.method !== person.firstMethod ? " (" + person.method + ")" : "") + ".", { contactLogPerson: person.id });
  return Object.assign({}, loan, { contactLog: log.concat([e]).slice(-CONTACT_LOG_MAX) });
}
// Is this lead do-not-contact (either spelling of the flag)?
export function leadIsDnc(lead) { return !!lead && !!(lead.doNotContact || lead.do_not_contact); }
// Do not contact on the lead — the same fields Marketing's markDNC writes.
export function leadDncPatch(lead, now) {
  if (!lead) return lead;
  if (lead.doNotContact && lead.do_not_contact !== false && lead.status === "DNC") return lead;
  return Object.assign({}, lead, { status: "DNC", doNotContact: true, do_not_contact: true, updated_at: now || new Date().toISOString() });
}

// ═══════ 8. aggregates (what the screen and the export show — no names) ═══════
const monthOf = function (d) { return d ? String(d).slice(0, 7) : "undated"; };
export function summarize(people, matches, flags) {
  const act = (people || []).filter(counts);
  const s = { people: act.length, byKind: { response: 0, file: 0, removal: 0 }, byMethod: {}, byMonth: {}, matched: { sure: 0, likely: 0, confirmed: 0, none: 0 }, check: 0, removals: { matched: 0, unmatched: 0, done: 0 }, notResponses: (people || []).filter(function (p) { return isActive(p) && p.decisions && p.decisions.notResponse; }).length, bySegment: {} };
  act.forEach(function (p) {
    s.byKind[p.kind] = (s.byKind[p.kind] || 0) + 1;
    s.byMethod[p.method] = (s.byMethod[p.method] || 0) + 1;
    const m = monthOf(p.firstAt);
    if (!s.byMonth[m]) s.byMonth[m] = { response: 0, file: 0, removal: 0, total: 0 };
    s.byMonth[m][p.kind]++; s.byMonth[m].total++;
    const mt = matches && matches.get(p.id);
    const k = mt ? mt.match : "none";
    s.matched[k] = (s.matched[k] || 0) + 1;
    const seg = mt && (mt.match === "sure" || mt.match === "confirmed") ? mt.segment : "unknown";
    s.bySegment[seg] = (s.bySegment[seg] || 0) + 1;
    if (openFlags(p, flags).length) s.check++;
    if (p.kind === "removal") {
      // done = the lead matched NOW is do-not-contact (never a record made for
      // another lead — review H)
      const ml = mt && (mt.match === "sure" || mt.match === "confirmed") ? mt.lead : null;
      if (leadIsDnc(ml)) s.removals.done++;
      if (mt && (mt.match === "sure" || mt.match === "confirmed") && mt.leadId) s.removals.matched++; else s.removals.unmatched++;
    }
  });
  return s;
}
// The export: aggregate counts only (never a name, number, email or id).
export function summaryCsv(s, meta) {
  const rows = [["What", "Count"]];
  const m = meta || {};
  if (m.files) rows.push(["Files", m.files]);
  rows.push(["People who reached out", s.people]);
  KINDS.forEach(function (k) { rows.push([KIND_LABEL[k] + "s", s.byKind[k] || 0]); });
  Object.keys(s.byMethod).sort().forEach(function (k) { rows.push(["By " + k, s.byMethod[k]]); });
  Object.keys(s.byMonth).sort().forEach(function (k) { const v = s.byMonth[k]; rows.push(["First reached out " + k, v.total], [" — responses " + k, v.response], [" — files " + k, v.file], [" — removal requests " + k, v.removal]); });
  ["sure", "confirmed", "likely", "none"].forEach(function (k) { rows.push(["Matched: " + MATCH_LABEL[k], s.matched[k] || 0]); });
  Object.keys(s.bySegment).sort().forEach(function (k) { rows.push(["Segment: " + k, s.bySegment[k]]); });
  rows.push(["Check these (open)", s.check], ["Marked not a response", s.notResponses]);
  return rows.map(function (r) { return r.map(function (v) { const x = String(v); return /[",\n]/.test(x) ? '"' + x.replace(/"/g, '""') + '"' : x; }).join(","); }).join("\r\n") + "\r\n";
}

// ═══════ 9. one record per PERSON for cost per response ═══════
const WEB_TAGS = ["web", "inbound-statement", "inbound-callback", "inbound-inquiry"];
function isWebLead(l) { return (l.tags || []).some(function (t) { return WEB_TAGS.indexOf(t) >= 0; }) || /^(ST|CB|IQ)-/.test(l.id || ""); }
function crmResponded(l) {
  return !!(l.responded || l.responded_at || normalizeMethod(l.response_method) || l.converted_loan_id || /^(Responded|Consultation|Application)$/.test(l.status || "") || (l.mailings || []).some(function (m) { return m && m.responded; }));
}
// A lead responsesForCost already counts on the CRM side (a responder or a
// web-intake lead) — a likely match to one is probably counted twice until
// he confirms it.
export function crmCountsLead(l) { return !!l && (isWebLead(l) || crmResponded(l)); }
function localDayOf(v) { if (!v) return null; const d = dayOf(v); if (d && /^\d{4}-\d{2}-\d{2}$/.test(String(v).trim())) return d; const t = new Date(v); if (isNaN(t.getTime())) return d; return t.getFullYear() + "-" + pad2(t.getMonth() + 1) + "-" + pad2(t.getDate()); }
// {mktgLeads, loans, contactPeople} → [{key, firstAt, method, methods, kind,
// segment, leadId?, loanId?, sources:["crm"|"web"|"call log"|"email log"],
// likelyLeadIds, possibleDupOf}]
//   CRM: every lead marked responded (responded / responded_at / a method /
//        a flagged mailing / a file it became / a responded status) and every
//        web-intake lead. Date = responseDay (responded_at, else the
//        credited mailing), web = the day it came in.
//   Log: every person who counts (not merged away, not "not a response").
//   One record per person: a log person and a lead are the same when the
//   person's match is the lead (sure / confirmed), or they share a phone /
//   email (never a number only said in a voicemail), or the same file.
//   Earliest date wins; kind file > removal > response.
//   Never linked automatically, but flagged: likelyLeadIds = a log person's
//   unconfirmed likely leads; possibleDupOf = the keys of the OTHER records
//   that are those leads — a probable double count (show a range, or leave
//   them out) until he confirms the match.
export function responsesForCost(input) {
  const o = input || {};
  const leads = (o.mktgLeads || []).filter(function (l) { return l && l.id; });
  const loans = (o.loans || []).filter(function (l) { return l && l.id; });
  const people = (o.contactPeople || []).filter(counts);
  const recs = [];
  const par = [];
  const find = function (i) { while (par[i] !== i) { par[i] = par[par[i]]; i = par[i]; } return i; };
  const union = function (a, b) { const x = find(a), y = find(b); if (x !== y) par[y] = x; };
  const byKey = new Map();
  const link = function (k, i) { if (!k) return; if (byKey.has(k)) union(byKey.get(k), i); else byKey.set(k, i); };
  const loanOfLead = new Map();
  loans.forEach(function (f) { if (f.sourceLeadId) loanOfLead.set(f.sourceLeadId, f.id); });
  leads.forEach(function (l) {
    const web = isWebLead(l);
    if (!web && !crmResponded(l)) return;
    const loanId = l.converted_loan_id || loanOfLead.get(l.id) || null;
    const day = responseDay(l) || (web ? localDayOf(l.created_at) : "") || null;
    // Only HOW they reached out ("Promoted" / "Mailer" aren't a method).
    const nm = normalizeMethod(l.response_method);
    const method = (RESPONSE_METHODS.indexOf(nm) >= 0 ? nm : "") || (web ? "Web form" : "");
    const kind = loanId ? "file" : (l.doNotContact || l.do_not_contact || /^(DNC|Opt-Out Requested)$/.test(l.status || "")) ? "removal" : "response";
    const i = recs.length;
    recs.push({ firstAt: day, method: method, kind: kind, segment: segmentOf(l), leadId: l.id, loanId: loanId, sources: [web ? "web" : "crm"], phones: [d10(l.phone)].filter(function (x) { return x.length === 10; }), emails: String(l.email || "").toLowerCase().split(/[;,\s]+/).filter(function (e) { return /@/.test(e); }) });
    par.push(i);
  });
  people.forEach(function (p) {
    const i = recs.length;
    const src = []; if (hasCall(p.method)) src.push("call log"); if (hasEmail(p.method)) src.push("email log");
    const linked = p.match === "sure" || p.match === "confirmed";
    recs.push({ firstAt: p.firstAt || null, method: p.method || "", kind: p.kind, segment: linked ? p.segment || "unknown" : "unknown", leadId: linked ? p.leadId || null : null, loanId: linked ? p.loanId || null : null, sources: src.length ? src : ["call log"], phones: p.phones || [], emails: p.emails || [], personId: p.id, likely: linked ? [] : p.likelyLeadIds || [] });
    par.push(i);
  });
  recs.forEach(function (r, i) {
    if (r.leadId) link("L" + r.leadId, i);
    if (r.loanId) link("F" + r.loanId, i);
    r.phones.forEach(function (x) { link("p" + x, i); });
    r.emails.forEach(function (x) { link("e" + x, i); });
  });
  const groups = new Map();
  recs.forEach(function (r, i) { const k = find(i); if (!groups.has(k)) groups.set(k, []); groups.get(k).push(r); });
  const out = [];
  groups.forEach(function (rs) {
    const dated = rs.filter(function (r) { return r.firstAt; }).sort(function (a, b) { return a.firstAt < b.firstAt ? -1 : a.firstAt > b.firstAt ? 1 : 0; });
    const first = dated[0] || rs[0];
    const methods = uniq(rs.map(function (r) { return r.method; }).filter(Boolean));
    const kind = rs.reduce(function (k, r) { return strongerKind(k, r.kind); }, "response");
    const seg = (rs.find(function (r) { return r.segment && r.segment !== "unknown" && r.leadId; }) || rs.find(function (r) { return r.segment && r.segment !== "unknown"; }) || {}).segment || "unknown";
    const leadId = (rs.find(function (r) { return r.leadId; }) || {}).leadId || null;
    const loanId = (rs.find(function (r) { return r.loanId; }) || {}).loanId || null;
    const personIds = rs.map(function (r) { return r.personId; }).filter(Boolean);
    const likely = uniq([].concat.apply([], rs.map(function (r) { return r.likely || []; }))).filter(function (id) { return !rs.some(function (r) { return r.leadId === id; }); });
    out.push({ key: leadId ? "L" + leadId : loanId ? "F" + loanId : personIds.length ? "P" + personIds[0] : "X" + out.length, firstAt: first.firstAt || null, method: first.method || methods[0] || "", methods: methods, kind: kind, segment: seg, leadId: leadId, loanId: loanId, sources: uniq([].concat.apply([], rs.map(function (r) { return r.sources; }))), likelyLeadIds: likely, possibleDupOf: [] });
  });
  const keyOfLead = new Map(); out.forEach(function (r) { if (r.leadId) keyOfLead.set(r.leadId, r.key); });
  out.forEach(function (r) { r.possibleDupOf = uniq(r.likelyLeadIds.map(function (id) { return keyOfLead.get(id); })).filter(function (k) { return k !== r.key; }); });
  out.sort(function (a, b) { return (a.firstAt || "9999") < (b.firstAt || "9999") ? -1 : (a.firstAt || "9999") > (b.firstAt || "9999") ? 1 : a.key < b.key ? -1 : 1; });
  return out;
}

// ═══════ 10. persistence — table contact_responses ═══════
// One row per person (supabase/2026-10-10-contact-responses.sql, NOT run
// yet). Per-row upserts only — never the whole table. localStorage
// "loanflow_contact_responses_<owner>" mirrors the people + the ids the
// server hasn't confirmed ("dirty"); it is the only copy while the table is
// missing (42P01 / PGRST205 → latched for the session, "Saved on this device
// only") or Supabase isn't configured. PostgREST's brief PGRST002 503 is a
// retryable error, never the latch. Practice mode writes nothing anywhere.
export const CR_TABLE = "contact_responses";
export const CR_SQL_FILE = "supabase/2026-10-10-contact-responses.sql";
export const CR_LS_PREFIX = "loanflow_contact_responses_";
const CR_PAGE = 1000, CR_MAX_PAGES = 50, CR_CONCURRENCY = 4;
const CR_COLS = "id,owner,phones,emails,names,first_at,last_at,method,kind,segment,lead_id,loan_id,match,decisions,sources,detail,created_at,updated_at";
export function crKey(owner) { return CR_LS_PREFIX + (owner || "reece"); }
let crState = "unknown";
export function contactTableState() { return crState; }
export function resetContactTable() { crState = "unknown"; }
function columnMissing(err) { return !!err && (err.code === "42703" || err.code === "PGRST204" || /column .* does not exist|could not find the '[^']*' column/i.test(err.message || "")); }
export function crTableMissing(err) {
  if (!err || columnMissing(err) || err.code === "PGRST002") return false;
  return err.code === "42P01" || err.code === "PGRST205" || /could not find the table|relation "?[\w.]*contact_responses"? does not exist/i.test(err.message || "");
}
export function personToRow(p, owner) {
  return {
    id: p.id, owner: owner || "reece",
    phones: p.phones || [], emails: p.emails || [], names: p.names || [],
    first_at: p.firstAt || null, last_at: p.lastAt || null,
    method: p.method || "", kind: p.kind || "response", segment: p.segment || "unknown",
    lead_id: p.leadId || null, loan_id: p.loanId || null, match: p.match || "none",
    decisions: p.decisions || {}, sources: p.sources || [],
    detail: { loanNos: p.loanNos || [], firstCallAt: p.firstCallAt || null, firstEmailAt: p.firstEmailAt || null, firstMethod: p.firstMethod || "", reasons: p.reasons || [], said: p.said || "", rows: p.rows || 0, facts: p.facts || {}, callbackPhones: p.callbackPhones || [], refs: p.refs || [], likelyLeadIds: p.likelyLeadIds || [] },
    created_at: p.createdAt || p.updatedAt || new Date().toISOString(), updated_at: p.updatedAt || new Date().toISOString(),
  };
}
export function rowToPerson(r) {
  if (!r || !r.id) return null;
  const d = r.detail || {};
  return {
    id: r.id, phones: r.phones || [], callbackPhones: d.callbackPhones || [], emails: r.emails || [], refs: d.refs || [], names: r.names || [], loanNos: d.loanNos || [],
    firstAt: dayOf(r.first_at) || null, lastAt: dayOf(r.last_at) || null, firstCallAt: d.firstCallAt || null, firstEmailAt: d.firstEmailAt || null,
    method: r.method || "", firstMethod: d.firstMethod || (hasEmail(r.method) && !hasCall(r.method) ? "Email" : "Call"),
    kind: KIND_RANK[r.kind] ? r.kind : "response", reasons: d.reasons || [], said: d.said || "", sources: r.sources || [], rows: d.rows || 0, facts: d.facts || {},
    segment: r.segment || "unknown", leadId: r.lead_id || null, loanId: r.loan_id || null, match: r.match || "none", likelyLeadIds: d.likelyLeadIds || [],
    decisions: r.decisions || {}, createdAt: r.created_at || null, updatedAt: r.updated_at || null,
  };
}
function lsLoad(owner) {
  try {
    const raw = window.localStorage.getItem(crKey(owner));
    if (!raw) return { people: [], dirty: [] };
    const o = JSON.parse(raw);
    const seen = new Map();
    (o && Array.isArray(o.people) ? o.people : []).forEach(function (p) { if (p && p.id && (!seen.has(p.id) || msOf(p.updatedAt) > msOf(seen.get(p.id).updatedAt))) seen.set(p.id, p); });
    return { people: Array.from(seen.values()), dirty: (o && Array.isArray(o.dirty) ? o.dirty : []).filter(function (id) { return seen.has(id); }) };
  } catch (e) { return { people: [], dirty: [] }; }
}
// read → merge (newest updatedAt per person) → write: another tab's people
// are never dropped by this one.
function lsMerge(owner, people, dirtyAdd, dirtyClear) {
  try {
    const cur = lsLoad(owner);
    const map = new Map(); cur.people.forEach(function (p) { map.set(p.id, p); });
    (people || []).forEach(function (p) { const had = map.get(p.id); if (!had || msOf(p.updatedAt) >= msOf(had.updatedAt)) map.set(p.id, p); });
    const dirty = new Set(cur.dirty);
    (dirtyAdd || []).forEach(function (id) { dirty.add(id); });
    (dirtyClear || []).forEach(function (id) { dirty.delete(id); });
    window.localStorage.setItem(crKey(owner), JSON.stringify({ v: 1, people: Array.from(map.values()), dirty: Array.from(dirty) }));
    return true;
  } catch (e) { return false; }
}
function warnMissing(err) { try { console.warn("LoanFlow: the contact_responses table isn't there yet — the call & email log is kept on this device until " + CR_SQL_FILE + " is run.", err && (err.message || err)); } catch (e) { /* ignore */ } }
async function fetchAllRows(db, owner) {
  const out = [];
  for (let n = 0; n < CR_MAX_PAGES; n++) {
    const from = n * CR_PAGE;
    const r = await db.from(CR_TABLE).select(CR_COLS).eq("owner", owner).order("created_at", { ascending: true }).order("id", { ascending: true }).range(from, from + CR_PAGE - 1);
    if (!r || r.error) throw (r && r.error) || new Error("No reply from the database.");
    const rows = Array.isArray(r.data) ? r.data : [];
    rows.forEach(function (x) { out.push(x); });
    if (rows.length < CR_PAGE) break;
  }
  return out;
}
async function upsertOne(db, p, owner) {
  const r = await db.from(CR_TABLE).upsert(personToRow(p, owner), { onConflict: "id" }).select("id");
  if (!r || r.error) throw (r && r.error) || new Error("No reply from the database.");
  if (!Array.isArray(r.data) || !r.data.some(function (x) { return x && x.id === p.id; })) throw new Error("The database didn't confirm this person.");
  return true;
}
// This device's copy (people + unconfirmed ids) — the screen takes in another
// tab's writes from it (its "storage" event).
export function readContactDevice(owner) { return lsLoad(owner || "reece"); }
// Per-row writes, a few at a time. Each person is first merged with this
// device's copy of them (another tab's decisions on the same person are kept,
// key by key — review F). → {saved, failed, mode, localReason, error, people:
// what was written}
export async function saveContactPeople(people, ctx) {
  const c = ctx || {};
  const owner = c.owner || "reece";
  let list = (people || []).filter(function (p) { return p && p.id; });
  if (c.practice) return { saved: 0, failed: 0, mode: "practice", localReason: null, error: null, people: list };
  const dev = new Map(lsLoad(owner).people.map(function (p) { return [p.id, p]; }));
  list = list.map(function (p) { return mergePersonVersions(p, dev.get(p.id)); });
  const lsOk = lsMerge(owner, list, list.map(function (p) { return p.id; }), []);
  if (!c.db || !c.ready) return { saved: 0, failed: 0, mode: "local", localReason: "offline", error: lsOk ? null : "This device's copy couldn't be written.", people: list };
  if (crState === "missing") return { saved: 0, failed: 0, mode: "local", localReason: "no_table", error: lsOk ? null : "This device's copy couldn't be written.", people: list };
  let saved = 0, failed = 0, error = null, missing = false;
  const done = [];
  let next = 0;
  const worker = async function () {
    while (next < list.length && !missing) {
      const p = list[next++];
      try { await upsertOne(c.db, p, owner); crState = "present"; saved++; done.push(p.id); }
      catch (e) {
        if (crTableMissing(e)) { missing = true; if (crState !== "missing") { crState = "missing"; warnMissing(e); } }
        else { failed++; error = (e && e.message) || String(e); }
      }
    }
  };
  const n = Math.max(1, Math.min(CR_CONCURRENCY, list.length));
  const ws = []; for (let i = 0; i < n; i++) ws.push(worker());
  await Promise.all(ws);
  lsMerge(owner, [], [], done);
  if (missing) return { saved: saved, failed: 0, mode: "local", localReason: "no_table", error: null, people: list };
  return { saved: saved, failed: failed, mode: "server", localReason: null, error: error, people: list };
}
// Load: the server's rows merged with this device's copy (the newer copy's
// evidence, decisions key by key), then this device's unconfirmed people go
// up one row at a time. Two copies of one person (two devices that imported
// before the SQL was run) fold into one — the table's row kept (review G).
// This device's copy is written read-merge-write, so another tab's save made
// while this was reading is never dropped (review E).
// → {people, mode, localReason, error, pushed}
export async function loadContactPeople(ctx) {
  const c = ctx || {};
  const owner = c.owner || "reece";
  const local = lsLoad(owner);
  const localOnly = function (reason) {
    const f = foldDuplicates(local.people);
    // Practice mode writes nothing anywhere — not even this device's copy.
    if (f.changed.length && !c.practice) lsMerge(owner, f.people.filter(function (p) { return f.changed.indexOf(p.id) >= 0; }), f.changed, []);
    return { people: f.people, mode: "local", localReason: reason, error: null, pushed: 0 };
  };
  if (!c.db || !c.ready) return localOnly("offline");
  if (crState === "missing") return localOnly("no_table");
  let rows;
  try { rows = await fetchAllRows(c.db, owner); }
  catch (e) {
    if (crTableMissing(e)) { crState = "missing"; warnMissing(e); return localOnly("no_table"); }
    return { people: local.people, mode: "unknown", localReason: null, error: (e && e.message) || String(e), pushed: 0 };
  }
  crState = "present";
  const server = rows.map(rowToPerson).filter(Boolean);
  const S = new Map(); server.forEach(function (p) { S.set(p.id, p); });
  const dirty = new Set(local.dirty);
  const pushIds = new Set(), confirmed = [];
  const out = server.slice();
  const at = new Map(); out.forEach(function (p, i) { at.set(p.id, i); });
  local.people.forEach(function (l) {
    const s = S.get(l.id);
    if (!s) { if (dirty.has(l.id) && !at.has(l.id)) { at.set(l.id, out.length); out.push(l); pushIds.add(l.id); } return; }
    const m = mergePersonVersions(s, l);
    if (m !== s) { out[at.get(l.id)] = m; pushIds.add(l.id); }
    else if (dirty.has(l.id)) confirmed.push(l.id);
  });
  const f = foldDuplicates(out, { prefer: function (id) { return S.has(id); } });
  f.changed.forEach(function (id) { pushIds.add(id); });
  const people = f.people;
  const push = people.filter(function (p) { return pushIds.has(p.id); });
  // Practice mode writes nothing anywhere — not even this device's copy.
  if (!c.practice) lsMerge(owner, people, Array.from(pushIds), confirmed);
  let pushed = 0;
  if (push.length && !c.practice) { const r = await saveContactPeople(push, c); pushed = r.saved; }
  return { people: people, mode: crState === "missing" ? "local" : "server", localReason: crState === "missing" ? "no_table" : null, error: null, pushed: pushed };
}
